import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { usePersistedState } from '../hooks/usePersistedState';
import { apiFetch } from '../services/api';
import * as api from '../services/api';
import KnowledgeGraph3D from '../components/KnowledgeGraph3D';
import PageHeader from '../components/common/PageHeader';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import { Sparkles, Bot, User, Send, ArrowRight, Loader, Brain } from 'lucide-react';
/* G103 — full v2 redesign of IntelligenceCenterPage. Replaces 100vh
 * flex layout + PageHeader + custom buttons with V2PageShell + V2Button.
 * v1 hooks/handlers/api calls preserved verbatim. */
import {
  V2PageShell,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';

/* ─── Platform Stone Palette (strict adherence) ─────────────
   stone-50:  #fafaf9     stone-100: #f5f5f4
   stone-200: #e7e5e4     stone-400: #a8a29e
   stone-500: #78716c     stone-600: #57534e
   stone-700: #44403c     stone-900: #1c1917
   Brand primary: #0d9488   Brand dark: #0a766b   Brand light: #e3f4f1
   ──────────────────────────────────────────────────────────── */
const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.08)',
  // Warm mid-tones for panels and backgrounds
  mid: '#d4453a',       // slightly deeper than primary — gradient anchor
  soft: '#f8d7d4',      // soft warm blush — panel backgrounds
  muted: '#eab3ad',     // muted rose — borders on warm panels
  tint: '#fff5f4',      // barely-there warm tint — card hover / alt backgrounds
};
const STONE = {
  50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4',
  400: '#a8a29e', 500: '#78716c', 600: '#57534e',
  700: '#44403c', 900: '#1c1917',
};

/* Severity uses only stone tones + brand for critical */
const SEV = {
  info: STONE[400], warning: STONE[600], error: BRAND.primary, critical: BRAND.dark,
};
const STATUS_MAP = {
  healthy: STONE[700], degraded: STONE[500], unhealthy: BRAND.primary, unreachable: BRAND.dark,
};
const ACTION_STATUS_MAP = {
  pending: STONE[500], approved: STONE[700], executing: STONE[600],
  completed: STONE[900], failed: BRAND.primary, rolled_back: STONE[400], dismissed: STONE[400],
};
const MODE_META = {
  recommend: { label: 'Recommend + Approve', desc: 'AI suggests, you decide', icon: '◎' },
  auto_fix:  { label: 'Auto-Fix (Low Risk)',  desc: 'Auto-fix low risk, escalate high risk', icon: '⟳' },
  autonomous:{ label: 'Full Autonomous',       desc: 'AI handles all with rollback', icon: '◉' },
};

/* ─── Shared Components ──────────────────────────────────── */
const Card = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{
    background: '#fff', border: `1px solid ${STONE[200]}`, borderRadius: 14,
    padding: 20, cursor: onClick ? 'pointer' : 'default',
    transition: 'box-shadow 0.15s', ...style,
  }}>
    {children}
  </div>
);

/**
 * Stat tile — uniform with the Test & Verify CTA gradient palette
 * (light primary tint with a subtle tinted border) so the Intelligence
 * Center reads as a calm command surface instead of a wall of solid
 * colour. Used for every overview metric across the page.
 */
function StatCard({ label, value, subtitle }) {
  return (
    <div style={{
      flex: 1, minWidth: 150,
      background: 'linear-gradient(180deg, #fef6f4 0%, #ffffff 75%)',
      border: `1px solid ${BRAND.primary}3d`,
      borderRadius: 12, padding: '14px 16px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: BRAND.primary,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
      }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 750, color: STONE[900],
        lineHeight: 1, letterSpacing: '-0.02em',
      }}>{value}</div>
      {subtitle && (
        <div style={{ fontSize: 11, color: STONE[500], marginTop: 4 }}>{subtitle}</div>
      )}
    </div>
  );
}

function SectionHeader({ title, badge, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 10, borderBottom: `2px solid ${BRAND.light}` }}>
      <h2 style={{ fontSize: 15, fontWeight: 650, color: STONE[900], margin: 0, flex: 1 }}>{title}</h2>
      {badge && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: BRAND.light, color: BRAND.primary }}>{badge}</span>}
      {action}
    </div>
  );
}

function Badge({ label, color }) {
  const c = color || STONE[500];
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: `${c}14`, color: c, textTransform: 'uppercase',
    }}>{label}</span>
  );
}

const btnStyle = (variant) => {
  const isPrimary = variant === 'primary';
  return {
    padding: '4px 12px', borderRadius: 6, fontSize: 10, fontWeight: 700,
    border: `1px solid ${isPrimary ? BRAND.primary : STONE[200]}`,
    background: isPrimary ? BRAND.light : '#fff',
    color: isPrimary ? BRAND.primary : STONE[600],
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
  };
};

/* ─── Checkbox (shared) ──────────────────────────────────── */
const Checkbox = ({ checked, onChange, style }) => (
  <div onClick={(e) => { e.stopPropagation(); onChange(!checked); }} style={{
    width: 16, height: 16, borderRadius: 4, flexShrink: 0, cursor: 'pointer',
    border: checked ? `2px solid ${BRAND.primary}` : `2px solid ${STONE[300] || '#d6d3d1'}`,
    background: checked ? BRAND.primary : '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.1s', ...style,
  }}>
    {checked && <span style={{ color: '#fff', fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
  </div>
);

/* ─── Bulk Action Bar ────────────────────────────────────── */
function BulkActionBar({ count, total, onSelectAll, onDeselectAll, actions }) {
  if (count === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
      background: BRAND.light, border: `1px solid ${BRAND.primary}30`,
      borderRadius: 8, marginBottom: 12,
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: BRAND.primary }}>
        {count} selected
      </span>
      <button onClick={onSelectAll} style={{
        fontSize: 10, fontWeight: 600, color: STONE[600], background: 'none',
        border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
      }}>Select all ({total})</button>
      <button onClick={onDeselectAll} style={{
        fontSize: 10, fontWeight: 600, color: STONE[600], background: 'none',
        border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
      }}>Deselect all</button>
      <span style={{ flex: 1 }} />
      {actions.map((a, i) => (
        <button key={i} onClick={a.onClick} disabled={a.disabled} style={{
          ...btnStyle(a.variant || 'primary'),
          opacity: a.disabled ? 0.5 : 1,
          cursor: a.disabled ? 'not-allowed' : 'pointer',
        }}>{a.label}</button>
      ))}
    </div>
  );
}

/* ─── Action Row ─────────────────────────────────────────── */
function ActionRow({ action, onApprove, onDismiss, onRollback, selected, onToggleSelect }) {
  const statusColor = ACTION_STATUS_MAP[action.status] || STONE[500];
  return (
    <div style={{
      padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
      background: selected ? BRAND.glow : 'transparent', transition: 'background 0.1s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        {onToggleSelect && <Checkbox checked={selected} onChange={onToggleSelect} />}
        <Badge label={action.status} color={statusColor} />
        <Badge label={action.actionType} />
        <Badge label={action.severity} color={SEV[action.severity]} />
        <span style={{ flex: 1, fontWeight: 600, color: STONE[900], fontSize: 13 }}>{action.title}</span>
      </div>
      <div style={{ color: STONE[500], marginBottom: 6, marginLeft: onToggleSelect ? 26 : 0 }}>{action.description}</div>
      {action.rootCause && <div style={{ fontSize: 11, color: STONE[600], marginBottom: 6, marginLeft: onToggleSelect ? 26 : 0 }}>Root cause: {action.rootCause}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: onToggleSelect ? 26 : 0 }}>
        <span style={{ fontSize: 10, color: STONE[400] }}>Target: {action.targetType}/{action.targetName}</span>
        <span style={{ flex: 1 }} />
        {action.status === 'pending' && (
          <>
            <button onClick={() => onApprove(action.id)} style={btnStyle('primary')}>Approve</button>
            <button onClick={() => onDismiss(action.id)} style={btnStyle()}>Dismiss</button>
          </>
        )}
        {action.status === 'completed' && action.rollbackData && (
          <button onClick={() => onRollback(action.id)} style={btnStyle()}>Rollback</button>
        )}
        {action.status === 'rolled_back' && <span style={{ fontSize: 10, color: STONE[400] }}>Rolled back at {action.rolledBackAt}</span>}
      </div>
    </div>
  );
}

function InsightRow({ insight, onAck, selected, onToggleSelect }) {
  const sevColor = SEV[insight.severity] || STONE[500];
  return (
    <div style={{
      padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
      background: selected ? BRAND.glow : 'transparent', transition: 'background 0.1s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {onToggleSelect && <Checkbox checked={selected} onChange={onToggleSelect} />}
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor, flexShrink: 0 }} />
        <Badge label={insight.insightType} color={sevColor} />
        <span style={{ fontWeight: 600, color: STONE[900], fontSize: 13, flex: 1 }}>{insight.title}</span>
        {insight.confidence && <span style={{ fontSize: 10, color: STONE[400] }}>{parseFloat(insight.confidence).toFixed(0)}%</span>}
      </div>
      <div style={{ color: STONE[500], marginBottom: 4, marginLeft: onToggleSelect ? 24 : 0 }}>{insight.description}</div>
      {insight.suggestedAction && <div style={{ fontSize: 11, color: STONE[600], marginBottom: 4, marginLeft: onToggleSelect ? 24 : 0 }}>Suggested: {insight.suggestedAction}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: onToggleSelect ? 24 : 0 }}>
        <span style={{ fontSize: 10, color: STONE[400] }}>{insight.category}</span>
        <span style={{ flex: 1 }} />
        {!insight.acknowledged && <button onClick={() => onAck(insight.id)} style={btnStyle('primary')}>Acknowledge</button>}
      </div>
    </div>
  );
}

function EventRow({ event, onDrillDown }) {
  const sevColor = SEV[event.severity] || STONE[400];
  const time = event.createdAt ? new Date(event.createdAt).toLocaleTimeString() : '';
  return (
    <div
      onClick={() => event.resourceId && onDrillDown(event.resourceType || 'event', event.resourceId)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
        borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
        cursor: event.resourceId ? 'pointer' : 'default',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (event.resourceId) e.currentTarget.style.background = STONE[50]; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor, flexShrink: 0 }} />
      <span style={{ width: 60, color: STONE[400], fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>{time}</span>
      <Badge label={event.severity?.toUpperCase()} color={sevColor} />
      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: STONE[100], color: STONE[500] }}>{event.eventCategory}</span>
      <span style={{ flex: 1, color: STONE[700], fontWeight: 500 }}>{event.eventType}</span>
      <span style={{ color: STONE[400], fontSize: 10 }}>{event.sourceService}</span>
      {event.resourceId && <span style={{ fontSize: 10, color: STONE[600] }}>→</span>}
    </div>
  );
}

/* ─── Structured Chat Message Renderer ──────────────────── */
function ChatMessageContent({ msg, onDrillDown, onCreateAction, onFollowUp }) {
  const ctx = msg.contextData;

  if (msg.role === 'user') {
    return <span>{msg.content}</span>;
  }

  // Assistant message with structured data
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Summary text */}
      <div style={{ fontSize: 12, lineHeight: 1.6, color: STONE[900] }}>{msg.content}</div>

      {/* Metrics row */}
      {ctx?.metrics && ctx.metrics.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ctx.metrics.map((m, i) => (
            <div key={i} style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
              background: m.status === 'critical' ? BRAND.light : m.status === 'warning' ? `${STONE[500]}14` : STONE[100],
              color: m.status === 'critical' ? BRAND.primary : m.status === 'warning' ? STONE[600] : STONE[700],
              border: `1px solid ${m.status === 'critical' ? BRAND.primary + '30' : STONE[200]}`,
            }}>
              {m.label}: {typeof m.value === 'number' ? m.value.toLocaleString() : m.value}
            </div>
          ))}
        </div>
      )}

      {/* Detail tables */}
      {ctx?.details && ctx.details.length > 0 && ctx.details.map((table, ti) => (
        table.rows && table.rows.length > 0 && (
          <div key={ti} style={{ borderRadius: 8, border: `1px solid ${STONE[200]}`, overflow: 'hidden' }}>
            {table.title && (
              <div style={{ padding: '6px 10px', background: STONE[50], fontSize: 10, fontWeight: 700, color: STONE[600], borderBottom: `1px solid ${STONE[200]}` }}>
                {table.title}
              </div>
            )}
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr>
                    {(table.columns || []).map((col, ci) => (
                      <th key={ci} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 700, color: STONE[600], borderBottom: `1px solid ${STONE[200]}`, background: STONE[50] }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.slice(0, 15).map((row, ri) => (
                    <tr key={ri} style={{ borderBottom: `1px solid ${STONE[100]}` }}>
                      {(Array.isArray(row) ? row : []).map((cell, ci) => (
                        <td key={ci} style={{ padding: '3px 8px', color: STONE[700] }}>{String(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}

      {/* Findings */}
      {ctx?.findings && ctx.findings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {ctx.findings.slice(0, 8).map((f, i) => (
            <div key={i}
              onClick={() => f.targetId && onDrillDown && onDrillDown(f.targetType, f.targetId)}
              style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 11,
                background: STONE[50], border: `1px solid ${STONE[200]}`,
                cursor: f.targetId ? 'pointer' : 'default',
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                background: SEV[f.severity] || STONE[400],
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: STONE[900], marginBottom: 2 }}>{f.title}</div>
                <div style={{ color: STONE[500], fontSize: 10 }}>{f.description}</div>
              </div>
              {f.targetId && <span style={{ color: STONE[400], fontSize: 10 }}>→</span>}
            </div>
          ))}
        </div>
      )}

      {/* Suggested actions */}
      {ctx?.suggestedActions && ctx.suggestedActions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ctx.suggestedActions.map((a, i) => (
            <button key={i}
              onClick={() => onCreateAction && onCreateAction(a)}
              style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                border: `1px solid ${BRAND.primary}30`, background: BRAND.light,
                color: BRAND.primary, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Follow-up questions */}
      {ctx?.followUp && ctx.followUp.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {ctx.followUp.map((q, i) => (
            <button key={i}
              onClick={() => onFollowUp && onFollowUp(q)}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 500,
                border: `1px solid ${STONE[200]}`, background: STONE[50],
                color: STONE[600], cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── AI Chat Panel ──────────────────────────────────────── */
function AIChatPanel({ sessionId, setSessionId, onDrillDown }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (sessionId) {
      api.intelligenceChatHistory(sessionId).then(r => {
        setMessages(r?.data || r || []);
      }).catch(() => {});
    }
  }, [sessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msgText = text || input.trim();
    if (!msgText || sending) return;
    const userMsg = { role: 'user', content: msgText, createdAt: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    try {
      const sid = sessionId || crypto.randomUUID();
      if (!sessionId) setSessionId(sid);
      const result = await api.intelligenceChat(sid, msgText);
      const data = result?.data || result;
      setMessages(prev => [...prev, data]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Analysis failed: ' + err.message, createdAt: new Date().toISOString() }]);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleCreateAction = async (actionData) => {
    try {
      const body = {
        targetType: actionData.targetType,
        targetId: actionData.targetId || null,
        targetName: actionData.targetName || actionData.label,
        title: actionData.label,
        description: actionData.description,
        severity: actionData.severity || 'medium',
      };
      // G581 — apiFetch is positional: (path, method='GET', body=null, ...).
      // Passing {method,body} as the 2nd arg silently made the HTTP method
      // an object and dropped the body, so the create-action call was a
      // no-op. Mirror the customer-helper fix in services/api.js.
      await apiFetch('/api/intelligence/actions', 'POST', body);
      setMessages(prev => [...prev, {
        role: 'assistant', content: 'Action created: ' + actionData.label + '. Check the Actions tab to manage it.',
        createdAt: new Date().toISOString(),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant', content: 'Failed to create action: ' + err.message,
        createdAt: new Date().toISOString(),
      }]);
    }
  };

  const quickActions = [
    { label: 'Errors & root causes', q: 'Show me all errors and their root causes' },
    { label: 'Connector health', q: 'Analyze connector health and any issues' },
    { label: 'Policy analysis', q: 'Show policy enforcement analysis and violations' },
    { label: 'Platform overview', q: 'Give me a full platform health overview' },
    { label: 'Workflow status', q: 'Analyze all workflow statuses and issues' },
  ];

  const aiColor = '#7c3aed'; // purple — intelligence theme color

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header — gradient with icon container, matching Template Builder */}
      <div style={{
        padding: '14px 16px', borderBottom: `1px solid ${STONE[200]}`,
        background: `linear-gradient(135deg, ${aiColor}08, ${aiColor}03)`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: aiColor + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Sparkles size={14} color={aiColor} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: STONE[900] }}>Intelligence AI</div>
          <div style={{ fontSize: 10, color: STONE[500] }}>Analyze operations, cross-correlate findings, and recommend fixes</div>
        </div>
      </div>

      {/* Messages — with avatars */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 300 }}>
        {/* Suggestions — shown only when no messages */}
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: STONE[400], textTransform: 'uppercase', marginBottom: 2 }}>Quick Actions</div>
            {quickActions.map(qa => (
              <button key={qa.label} onClick={() => sendMessage(qa.q)}
                style={{
                  padding: '8px 12px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                  border: `1px solid ${STONE[200]}`, background: STONE[50], color: STONE[700],
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', width: '100%',
                  display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.4,
                }}>
                <ArrowRight size={11} color={aiColor} style={{ flexShrink: 0 }} />
                {qa.label}
              </button>
            ))}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
            {/* Avatar */}
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: msg.role === 'user' ? BRAND.primary : aiColor + '15',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {msg.role === 'user' ? <User size={12} color="#fff" /> : <Bot size={12} color={aiColor} />}
            </div>
            {/* Bubble */}
            <div style={{
              maxWidth: msg.role === 'user' ? '85%' : '92%',
              padding: '10px 14px', borderRadius: 12,
              borderTopRightRadius: msg.role === 'user' ? 4 : 12,
              borderTopLeftRadius: msg.role === 'user' ? 12 : 4,
              background: msg.role === 'user' ? BRAND.primary : STONE[50],
              color: msg.role === 'user' ? '#fff' : STONE[900],
              fontSize: 12, lineHeight: 1.6,
            }}>
              <ChatMessageContent
                msg={msg}
                onDrillDown={onDrillDown}
                onCreateAction={handleCreateAction}
                onFollowUp={(q) => sendMessage(q)}
              />
            </div>
          </div>
        ))}
        {/* Loading — with bot avatar and animated dots */}
        {sending && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: aiColor + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={12} color={aiColor} />
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 12, borderTopLeftRadius: 4, background: STONE[50], fontSize: 12, color: STONE[500] }}>
              <span className="ai-typing-intel">Analyzing platform data</span>
              <style>{`.ai-typing-intel::after { content: '...'; animation: dotsi 1.5s infinite; } @keyframes dotsi { 0%,20% { content: '.'; } 40% { content: '..'; } 60%,100% { content: '...'; } }`}</style>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input — stone background footer */}
      <div style={{ padding: 12, borderTop: `1px solid ${STONE[200]}`, background: STONE[50], display: 'flex', gap: 8 }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask about errors, connectors, workflows..."
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            border: `1px solid ${STONE[200]}`, outline: 'none', fontFamily: 'inherit',
            background: '#fff', color: STONE[900],
          }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={!input.trim() || sending}
          style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: (!input.trim() || sending) ? STONE[200] : aiColor,
            color: '#fff', border: 'none',
            cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
        >
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}

/* ─── Drill-Down Panel ───────────────────────────────────── */
function DrillDownPanel({ targetType, targetId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.intelligenceDrillDown(targetType, targetId)
      .then(r => setData(r?.data || r))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [targetType, targetId]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: STONE[400] }}>Loading details...</div>;
  if (!data) return <div style={{ padding: 32, textAlign: 'center', color: BRAND.primary }}>Failed to load details</div>;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button onClick={onClose} style={btnStyle()}>← Back</button>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: STONE[900], margin: 0, flex: 1 }}>
          Drill-Down: {targetType}/{targetId.toString().substring(0, 8)}...
        </h3>
      </div>

      {data.workflowInfo && (
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader title="Workflow Details" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
            {Object.entries(data.workflowInfo).map(([k, v]) => (
              <div key={k}><span style={{ color: STONE[400] }}>{k}:</span> <span style={{ fontWeight: 600, color: STONE[900] }}>{String(v)}</span></div>
            ))}
          </div>
        </Card>
      )}
      {data.agentInfo && (
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader title="Agent Details" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
            {Object.entries(data.agentInfo).map(([k, v]) => (
              <div key={k}><span style={{ color: STONE[400] }}>{k}:</span> <span style={{ fontWeight: 600, color: STONE[900] }}>{String(v)}</span></div>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader title="Related Events" badge={`${data.eventCount || 0}`} />
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {(data.relatedEvents || []).map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: `1px solid ${STONE[100]}`, fontSize: 11 }}>
              <Badge label={e.severity} color={SEV[e.severity]} />
              <span style={{ color: STONE[700], flex: 1 }}>{e.eventType}</span>
              <span style={{ color: STONE[400], fontSize: 10 }}>{e.sourceService}</span>
            </div>
          ))}
          {!(data.relatedEvents || []).length && <div style={{ color: STONE[400], fontSize: 12, padding: 12, textAlign: 'center' }}>No related events</div>}
        </div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader title="Related Actions" badge={`${(data.relatedActions || []).length}`} />
        {(data.relatedActions || []).map((a, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: `1px solid ${STONE[100]}`, fontSize: 11 }}>
            <Badge label={a.status} color={ACTION_STATUS_MAP[a.status]} />
            <span style={{ color: STONE[900], fontWeight: 500, flex: 1 }}>{a.title}</span>
          </div>
        ))}
        {!(data.relatedActions || []).length && <div style={{ color: STONE[400], fontSize: 12, padding: 12, textAlign: 'center' }}>No related actions</div>}
      </Card>
    </div>
  );
}

/* ═══ Main Intelligence Center Page ══════════════════════════ */
/* ─── AI Model Configuration Tab Component ─────────────────────────────── */
const AVAILABLE_MODELS = [
  { provider: 'anthropic', modelId: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', tier: 'standard' },
  { provider: 'anthropic', modelId: 'claude-opus-4-6', name: 'Claude Opus 4.6', tier: 'premium' },
  { provider: 'anthropic', modelId: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', tier: 'fast' },
  { provider: 'openai', modelId: 'gpt-4o', name: 'GPT-4o', tier: 'standard' },
  { provider: 'openai', modelId: 'gpt-4o-mini', name: 'GPT-4o Mini', tier: 'fast' },
  { provider: 'google', modelId: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', tier: 'premium' },
  { provider: 'google', modelId: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', tier: 'fast' },
];

// Every AI capability the platform exposes. The page's "AI Model
// Configuration" tab renders one row per entry so a SuperAdmin can
// pick the model (Claude / GPT / Gemini) for each feature, with a
// Global Default as the fallback. Each row is also gated by the
// matching `features.ai.<flagKey>` flag in featureFlags.js — when
// the flag is OFF, the feature is disabled entirely tenant-wide.
//
// Order is roughly "most-touched-first" so a SuperAdmin scrolling top
// to bottom hits the daily-use surfaces (canvas builder, debugger,
// explainer) before the supporting infrastructure (template, policy
// extractor, eval).
const FEATURES = [
  { key: 'global',          name: 'Global Default',           description: 'Default model for all AI features unless overridden',                                          icon: '🌐', flagKey: null,                  groupHint: 'Default' },
  { key: 'ai_builder',      name: 'AI Workflow Builder',      description: 'Creates and modifies workflows from natural language (drives the AI bar Build action and chip-click apply)', icon: '🏗️', flagKey: 'canvasBuilder',       groupHint: 'Canvas authoring' },
  { key: 'debug',           name: 'Workflow Debugger',        description: 'Node-level debugging and data-flow analysis (drives the Debug button and recommendation chips)',     icon: '🔍', flagKey: 'canvasDebugger',      groupHint: 'Canvas authoring' },
  { key: 'explainer',       name: 'AI Node Explainer',        description: 'Tier-S #1 — Business + Technical breakdown of the selected node on the right Task tab in /canvas-v3',     icon: '🪄', flagKey: 'explainerPanel',      groupHint: 'Canvas authoring' },
  { key: 'recommendations', name: 'AI Recommendation Chips',  description: 'Tier-S #2 — surfaces the top 3 quickFixes from a Debug result as one-click apply chips above the AI bar', icon: '💡', flagKey: 'recommendationChips', groupHint: 'Canvas authoring' },
  { key: 'agent_chat',      name: 'Agent Chat',               description: 'Conversational AI inside the agent test-run flow (chat with an agent before publishing)',                  icon: '💬', flagKey: 'testRunChat',         groupHint: 'Agent runtime' },
  { key: 'intelligence',    name: 'Intelligence Center Chat', description: 'Operator chat in this page — ask about errors, connectors, policy, platform health',                       icon: '🧠', flagKey: 'intelligenceChat',    groupHint: 'Operator chat' },
  { key: 'template_generator', name: 'AI Template Generator', description: 'Generates reusable workflow templates from a prompt in the Template Center',                                icon: '📋', flagKey: 'templateGenerator',   groupHint: 'Templates' },
  { key: 'policy_extractor',   name: 'Policy Extractor',      description: 'Extracts enforcement patterns from uploaded policy / compliance documents (Policy Config admin)',           icon: '📜', flagKey: 'policyExtractor',     groupHint: 'Governance' },
  { key: 'eval',            name: 'Evaluation Engine',        description: 'Agent evaluation and automated test-suite runs',                                                            icon: '📊', flagKey: null,                  groupHint: 'Quality' },
];

const PROVIDER_COLORS = { anthropic: BRAND.primary, openai: STONE[600], google: STONE[500] };
const TIER_COLORS = { fast: STONE[500], standard: STONE[700], premium: BRAND.dark };

function AIModelConfigTab() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);

  const inputStyle = { width: '100%', padding: '8px 12px', border: `1px solid ${STONE[200]}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff', color: STONE[900] };

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    const res = await api.getModelConfigs();
    if (res?.ok) {
      const map = {};
      (res.data || []).forEach(c => { map[c.featureKey] = c; });
      setConfigs(map);
    }
    setLoading(false);
  };

  const handleSave = async (featureKey, modelId, maxTokens, temperature) => {
    setSaving(featureKey);
    const model = AVAILABLE_MODELS.find(m => m.modelId === modelId);
    const res = await api.upsertModelConfig(featureKey, {
      provider: model?.provider || 'anthropic',
      modelId,
      modelName: model?.name || modelId,
      maxTokens: maxTokens || 8192,
      temperature: temperature ?? 0.3,
      enabled: true,
    });
    if (res?.ok) {
      setConfigs(prev => ({ ...prev, [featureKey]: res.data }));
      setEditingFeature(null);
    }
    setSaving(null);
  };

  const handleRemoveOverride = async (featureKey) => {
    const res = await api.deleteModelConfig(featureKey);
    if (res?.ok !== false) {
      setConfigs(prev => {
        const next = { ...prev };
        delete next[featureKey];
        return next;
      });
    }
  };

  const globalConfig = configs['global'];

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: STONE[400] }}>Loading model configurations...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, color: STONE[500], margin: 0 }}>
          Configure which AI models power each platform feature. Set a global default, then optionally override individual features.
        </p>
      </div>

      {/* Feature Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {FEATURES.map(feature => {
          const config = configs[feature.key];
          const isGlobal = feature.key === 'global';
          const effectiveModel = config?.modelId || (isGlobal ? null : globalConfig?.modelId);
          const effectiveModelInfo = AVAILABLE_MODELS.find(m => m.modelId === effectiveModel);
          const isOverridden = !isGlobal && !!config;
          const isEditing = editingFeature === feature.key;

          return (
            <div key={feature.key} style={{
              background: '#fff', border: `1px solid ${isGlobal ? BRAND.primary + '40' : STONE[200]}`,
              borderRadius: 10, padding: '16px 20px', transition: 'box-shadow 0.15s',
              ...(isGlobal ? { borderWidth: 2, boxShadow: `0 0 0 1px ${BRAND.primary}15` } : {}),
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1 }}>
                  <span style={{ fontSize: 20 }}>{feature.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: STONE[900] }}>
                      {feature.name}
                      {isGlobal && <span style={{ fontSize: 10, background: BRAND.light, color: BRAND.primary, padding: '2px 8px', borderRadius: 8, marginLeft: 8, fontWeight: 600 }}>DEFAULT</span>}
                      {isOverridden && <span style={{ fontSize: 10, background: STONE[100], color: STONE[700], padding: '2px 8px', borderRadius: 8, marginLeft: 8, fontWeight: 600 }}>OVERRIDE</span>}
                    </div>
                    <div style={{ fontSize: 12, color: STONE[500], marginTop: 2 }}>{feature.description}</div>
                  </div>
                </div>

                {/* Current Model Display */}
                {!isEditing && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {effectiveModelInfo ? (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: STONE[900] }}>{effectiveModelInfo.name}</div>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 2 }}>
                          <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: (PROVIDER_COLORS[effectiveModelInfo.provider] || STONE[500]) + '15', color: PROVIDER_COLORS[effectiveModelInfo.provider] || STONE[500] }}>
                            {effectiveModelInfo.provider}
                          </span>
                          <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: (TIER_COLORS[effectiveModelInfo.tier] || STONE[500]) + '15', color: TIER_COLORS[effectiveModelInfo.tier] || STONE[500] }}>
                            {effectiveModelInfo.tier}
                          </span>
                          {!isGlobal && !isOverridden && <span style={{ fontSize: 9, color: STONE[400] }}>(inherited)</span>}
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: STONE[400], fontStyle: 'italic' }}>Not configured</span>
                    )}
                    <button onClick={() => setEditingFeature(feature.key)} style={{
                      border: `1px solid ${STONE[200]}`, background: STONE[50], color: STONE[600],
                      padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                    }}>
                      Configure
                    </button>
                  </div>
                )}
              </div>

              {/* Inline Editor */}
              {isEditing && (
                <ModelConfigEditor
                  feature={feature}
                  config={config}
                  globalConfig={globalConfig}
                  onSave={handleSave}
                  onRemoveOverride={handleRemoveOverride}
                  onCancel={() => setEditingFeature(null)}
                  saving={saving === feature.key}
                  inputStyle={inputStyle}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModelConfigEditor({ feature, config, globalConfig, onSave, onRemoveOverride, onCancel, saving, inputStyle }) {
  const [modelId, setModelId] = useState(config?.modelId || globalConfig?.modelId || 'claude-sonnet-4-6');
  const [maxTokens, setMaxTokens] = useState(config?.maxTokens || 8192);
  const [temperature, setTemperature] = useState(config?.temperature ?? 0.3);

  const isGlobal = feature.key === 'global';
  const hasOverride = !isGlobal && !!config;

  return (
    <div style={{ marginTop: 16, padding: 16, background: STONE[50], borderRadius: 8, border: `1px solid ${STONE[200]}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 10, fontWeight: 600, color: STONE[600], marginBottom: 4, display: 'block' }}>Model</label>
          <select value={modelId} onChange={e => setModelId(e.target.value)} style={inputStyle}>
            {AVAILABLE_MODELS.map(m => (
              <option key={m.modelId} value={m.modelId}>{m.name} ({m.provider} — {m.tier})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, fontWeight: 600, color: STONE[600], marginBottom: 4, display: 'block' }}>Max Tokens</label>
          <input type="number" value={maxTokens} onChange={e => setMaxTokens(Number(e.target.value))} style={inputStyle} min={256} max={32768} step={256} />
        </div>
        <div>
          <label style={{ fontSize: 10, fontWeight: 600, color: STONE[600], marginBottom: 4, display: 'block' }}>Temperature</label>
          <input type="number" value={temperature} onChange={e => setTemperature(Number(e.target.value))} style={inputStyle} min={0} max={1} step={0.1} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {hasOverride && (
          <button onClick={() => { onRemoveOverride(feature.key); onCancel(); }} style={{
            border: `1px solid ${STONE[200]}`, background: '#fef2f2', color: '#ef4444',
            padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
          }}>
            Remove Override
          </button>
        )}
        <button onClick={onCancel} style={{
          border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600],
          padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
        }}>
          Cancel
        </button>
        <button onClick={() => onSave(feature.key, modelId, maxTokens, temperature)} disabled={saving} style={{
          border: 'none', background: BRAND.primary, color: '#fff',
          padding: '6px 16px', borderRadius: 6, cursor: saving ? 'default' : 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
          opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ─── KNOWLEDGE GRAPH STATS PANEL ──────────────────────────────
// ═══════════════════════════════════════════════════════════════
function GraphStatsPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.intelligenceGraphStats().then(r => { if (r.ok) setStats(r.data); });
  }, []);
  if (!stats) return null;
  const healthDist = stats.healthDistribution || {};
  const topRisk = stats.topRiskNodes || [];
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Total Nodes" value={stats.totalNodes || 0} />
        <StatCard label="Total Edges" value={stats.totalEdges || 0} />
        <StatCard label="Healthy" value={healthDist.HEALTHY || 0} subtitle="95-100%" />
        <StatCard label="Degraded" value={healthDist.DEGRADED || 0} subtitle="80-94%" />
        <StatCard label="Critical" value={(healthDist.CRITICAL || 0) + (healthDist.IMPAIRED || 0)} subtitle="<80%" />
      </div>
      {topRisk.length > 0 && (
        <Card>
          <SectionHeader title="Top Risk Nodes" badge="Highest Blast Radius" />
          {topRisk.map((n, i) => (
            <div key={n.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${STONE[100]}` }}>
              <span style={{ fontWeight: 700, color: STONE[400], fontSize: 11, width: 20 }}>#{i + 1}</span>
              <Badge label={n.type} />
              <span style={{ fontSize: 13, fontWeight: 600, color: STONE[900], flex: 1 }}>{n.name}</span>
              <span style={{ fontSize: 11, color: STONE[500] }}>Health: {Math.round(n.health)}%</span>
              <Badge label={`Blast: ${n.blastRadius}`} color={n.blastRadius > 5 ? BRAND.primary : STONE[500]} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ─── OUTCOME TRACKING TAB ─────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
function OutcomeTrackingTab() {
  const [stats, setStats] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const pgOutcomes = usePaginationState(10);
  useEffect(() => {
    api.intelligenceOutcomeStats().then(r => { if (r.ok) setStats(r.data); });
    api.intelligenceOutcomes(48).then(r => { if (r.ok) setOutcomes(r.data || []); });
  }, []);

  const EFF_COLORS = {
    EFFECTIVE: '#059669', PARTIALLY_EFFECTIVE: '#D97706',
    INEFFECTIVE: STONE[500], MADE_WORSE: '#DC2626', PENDING: STONE[400],
  };
  const DUR_COLORS = {
    DURABLE: '#059669', TEMPORARY_FIX: '#D97706',
    REGRESSION: '#DC2626', PENDING: STONE[400],
  };

  return (
    <div>
      <SectionHeader title="Outcome Tracking" badge="Phase 1" />
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard label="Total Outcomes" value={stats.totalOutcomes || 0} />
          <StatCard label="Effective" value={stats.effective || 0} subtitle={`${stats.totalOutcomes > 0 ? Math.round((stats.effective / stats.totalOutcomes) * 100) : 0}%`} />
          <StatCard label="Made Worse" value={stats.madeWorse || 0} subtitle="Needs attention" />
          <StatCard label="Pending" value={stats.pending || 0} subtitle="Awaiting measurement" />
          <StatCard label="Last 24h" value={stats.last24hCount || 0} subtitle={`Avg delta: ${(stats.last24hAvgDelta || 0).toFixed(1)}`} />
        </div>
      )}

      <Card>
        <SectionHeader title="Recent Outcomes (48h)" badge={`${outcomes.length}`} />
        {outcomes.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
            No outcomes recorded yet. Execute Intelligence Actions to start tracking effectiveness.
          </div>
        ) : pgOutcomes.paginate(outcomes).map(o => (
          <div key={o.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Badge label={o.effectiveness || 'PENDING'} color={EFF_COLORS[o.effectiveness] || STONE[400]} />
              <Badge label={o.durability || 'PENDING'} color={DUR_COLORS[o.durability] || STONE[400]} />
              <span style={{ flex: 1, fontWeight: 600, color: STONE[900] }}>
                {o.targetNodeType} — Action {String(o.actionId).slice(0, 8)}...
              </span>
              <span style={{ fontSize: 11, color: o.healthDelta >= 0 ? '#059669' : '#DC2626', fontWeight: 700 }}>
                {o.healthDelta >= 0 ? '+' : ''}{(o.healthDelta || 0).toFixed(1)} health
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, fontSize: 10, color: STONE[400] }}>
              <span>Cool-down: {o.coolDownSeconds}s</span>
              {o.measuredAt && <span>Measured: {new Date(o.measuredAt).toLocaleString()}</span>}
              {o.cascadeEffects && <span>Cascade delta: {(o.cascadeEffects.dependentHealthDelta || 0).toFixed(1)}</span>}
            </div>
          </div>
        ))}
        <Pagination totalItems={outcomes.length} page={pgOutcomes.page} pageSize={pgOutcomes.pageSize} onPageChange={pgOutcomes.setPage} onPageSizeChange={pgOutcomes.setPageSize} noun="outcomes" />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ─── PATTERN MEMORY TAB ───────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
function PatternMemoryTab() {
  const [stats, setStats] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [subTab, setSubTab] = useState('overview');
  const [correlations, setCorrelations] = useState([]);
  const [corrStats, setCorrStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [causalGraph, setCausalGraph] = useState(null);
  const pgPatterns = usePaginationState(10);
  const pgCorr = usePaginationState(10);

  useEffect(() => {
    api.intelligencePatternStats().then(r => { if (r.ok) setStats(r.data); });
    loadPatterns();
    api.intelligenceCorrelationStats().then(r => { if (r.ok) setCorrStats(r.data); });
    api.intelligenceUpcomingPatterns(24).then(r => { if (r.ok) setUpcoming(r.data || []); });
  }, []);

  const loadPatterns = (type) => {
    api.intelligencePatterns(type).then(r => { if (r.ok) setPatterns(r.data || []); });
  };

  const loadCorrelations = () => {
    api.intelligenceCorrelations().then(r => { if (r.ok) setCorrelations(r.data || []); });
  };

  const loadCausalGraph = () => {
    api.intelligenceCausalGraph().then(r => { if (r.ok) setCausalGraph(r.data); });
  };

  const TYPE_COLORS = {
    TEMPORAL: STONE[600], CAUSAL: BRAND.dark,
    RESOLUTION: '#059669', ANTI_PATTERN: '#DC2626',
  };
  const CORR_COLORS = {
    co_occurrence: STONE[500], cascade: '#D97706', trigger: '#DC2626',
  };

  const confidenceBar = (val, width = 80) => (
    <div style={{ width, height: 6, background: STONE[200], borderRadius: 3, overflow: 'hidden' }}>
      <div style={{
        width: `${(val || 0) * 100}%`, height: '100%', borderRadius: 3,
        background: val >= 0.7 ? '#059669' : val >= 0.3 ? '#D97706' : STONE[500],
      }} />
    </div>
  );

  const SUB_TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'temporal', label: 'Temporal OSS' },
    { key: 'causal', label: 'Causal' },
    { key: 'resolution', label: 'Resolution' },
  ];

  return (
    <div>
      <SectionHeader title="Pattern Memory" badge="Phase 3" />

      {/* ─── Stats Row ─── */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <StatCard label="Total Patterns" value={stats.totalPatterns || 0} />
          <StatCard label="Active" value={stats.activePatterns || 0} />
          <StatCard label="High Confidence" value={stats.highConfidence || 0} subtitle=">0.7" />
          <StatCard label="Correlations" value={stats.totalCorrelations || 0} />
          <StatCard label="Upcoming (24h)" value={upcoming.length} subtitle="predicted" />
          <StatCard label="High-Success" value={stats.highSuccessResolutions || 0} subtitle=">70% rate" />
        </div>
      )}

      {/* ─── Sub-Tab Nav ─── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${STONE[200]}`, paddingBottom: 8 }}>
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => {
            setSubTab(t.key);
            if (t.key === 'causal' && correlations.length === 0) loadCorrelations();
            if (t.key === 'causal' && !causalGraph) loadCausalGraph();
          }}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: 'none',
              background: subTab === t.key ? BRAND.primary : 'transparent',
              color: subTab === t.key ? '#fff' : STONE[500],
              transition: 'all 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW SUB-TAB ═══ */}
      {subTab === 'overview' && (
        <div>
          {/* Type Filter */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {[null, 'TEMPORAL', 'CAUSAL', 'RESOLUTION', 'ANTI_PATTERN'].map(t => (
              <button key={t || 'all'} onClick={() => { setFilterType(t); loadPatterns(t); }}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${filterType === t ? BRAND.primary : STONE[200]}`,
                  background: filterType === t ? BRAND.primary : '#fff',
                  color: filterType === t ? '#fff' : STONE[600],
                }}>
                {t || 'All'}
              </button>
            ))}
          </div>

          <Card>
            <SectionHeader title="Active Patterns" badge={`${patterns.length}`} />
            {patterns.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No patterns learned yet. The system will detect patterns as events and outcomes accumulate.
              </div>
            ) : pgPatterns.paginate(patterns).map(p => (
              <div key={p.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Badge label={p.patternType} color={TYPE_COLORS[p.patternType] || STONE[500]} />
                  <span style={{ fontWeight: 600, color: STONE[900], flex: 1, fontSize: 13 }}>{p.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: STONE[400] }}>{(p.confidence * 100).toFixed(0)}%</span>
                    {confidenceBar(p.confidence)}
                  </div>
                </div>
                {p.description && <div style={{ color: STONE[500], marginBottom: 4, fontSize: 11 }}>{p.description}</div>}
                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: STONE[400] }}>
                  <span>Observations: {p.observationCount}</span>
                  {p.averageEffectiveness != null && <span>Avg effectiveness: {p.averageEffectiveness.toFixed(1)}</span>}
                  {p.successRate != null && p.successRate > 0 && <span>Success: {(p.successRate * 100).toFixed(0)}%</span>}
                  {p.recurrenceScore != null && p.recurrenceScore > 0 && <span>Recurrence: {(p.recurrenceScore * 100).toFixed(0)}%</span>}
                  {p.lastObservedAt && <span>Last seen: {new Date(p.lastObservedAt).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
            <Pagination totalItems={patterns.length} page={pgPatterns.page} pageSize={pgPatterns.pageSize} onPageChange={pgPatterns.setPage} onPageSizeChange={pgPatterns.setPageSize} noun="patterns" />
          </Card>
        </div>
      )}

      {/* ═══ TEMPORAL SUB-TAB ═══ */}
      {subTab === 'temporal' && (
        <div>
          {/* Upcoming Predictions */}
          <Card>
            <SectionHeader title="Upcoming Predictions (24h)" badge={`${upcoming.length}`} />
            {upcoming.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No patterns predicted to recur in the next 24 hours.
              </div>
            ) : upcoming.map(p => (
              <div key={p.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 16 }}>⏰</span>
                  <span style={{ fontWeight: 600, color: STONE[900], flex: 1 }}>{p.name}</span>
                  {p.recurrenceCron && (
                    <span style={{ fontSize: 10, color: STONE[400], fontFamily: 'monospace', background: STONE[100], padding: '2px 6px', borderRadius: 4 }}>
                      {p.recurrenceCron}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: STONE[400] }}>
                  {p.nextPredictedAt && <span style={{ color: BRAND.primary, fontWeight: 600 }}>
                    Next: {new Date(p.nextPredictedAt).toLocaleString()}
                  </span>}
                  <span>Recurrence: {((p.recurrenceScore || 0) * 100).toFixed(0)}%</span>
                  <span>Confidence: {((p.confidence || 0) * 100).toFixed(0)}%</span>
                  <span>Observations: {p.observationCount}</span>
                </div>
              </div>
            ))}
          </Card>

          {/* Temporal OSS Patterns List */}
          <Card style={{ marginTop: 16 }}>
            <SectionHeader title="All Temporal OSS Patterns" />
            {patterns.filter(p => p.patternType === 'TEMPORAL').length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No temporal patterns detected yet. The system learns from recurring failure events.
              </div>
            ) : patterns.filter(p => p.patternType === 'TEMPORAL').map(p => (
              <div key={p.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Badge label="TEMPORAL" color={TYPE_COLORS.TEMPORAL} />
                  <span style={{ fontWeight: 600, color: STONE[900], flex: 1 }}>{p.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: STONE[400] }}>
                      Recurrence: {((p.recurrenceScore || 0) * 100).toFixed(0)}%
                    </span>
                    {confidenceBar(p.recurrenceScore || 0, 60)}
                  </div>
                </div>
                {p.description && <div style={{ color: STONE[500], fontSize: 11, marginBottom: 4 }}>{p.description}</div>}
                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: STONE[400] }}>
                  {p.recurrenceCron && <span style={{ fontFamily: 'monospace' }}>Cron: {p.recurrenceCron}</span>}
                  <span>Observations: {p.observationCount}</span>
                  {p.lastObservedAt && <span>Last: {new Date(p.lastObservedAt).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ═══ CAUSAL SUB-TAB ═══ */}
      {subTab === 'causal' && (
        <div>
          {/* Correlation Stats */}
          {corrStats && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <StatCard label="Total Correlations" value={corrStats.total || 0} />
              <StatCard label="Active" value={corrStats.active || 0} />
              <StatCard label="High Confidence" value={corrStats.highConfidence || 0} subtitle=">0.7" />
              <StatCard label="Confirmed Causal" value={corrStats.confirmed || 0} subtitle="trigger type" />
            </div>
          )}

          {/* Causal Graph Summary */}
          {causalGraph && (
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader title="Causal Graph" badge={`${causalGraph.totalCorrelations || 0} edges`} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
                {(causalGraph.nodes || []).map(n => (
                  <div key={n.id} style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                    background: (n.outDegree || 0) > (n.inDegree || 0) ? '#FEF2F0' : '#F0FDF4',
                    border: `1px solid ${(n.outDegree || 0) > (n.inDegree || 0) ? '#F5B0A8' : '#BBF7D0'}`,
                    color: STONE[700],
                  }}>
                    {n.id}
                    <span style={{ fontSize: 9, marginLeft: 6, color: STONE[400] }}>
                      ↑{n.inDegree} ↓{n.outDegree}
                    </span>
                  </div>
                ))}
              </div>
              {(causalGraph.nodes || []).length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                  No causal graph edges detected yet. Correlations build as events co-occur.
                </div>
              )}
            </Card>
          )}

          {/* Correlations List */}
          <Card>
            <SectionHeader title="Event Correlations" badge={`${correlations.length}`} />
            {correlations.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No correlations detected yet. The engine discovers cause→effect chains from event sequences.
              </div>
            ) : pgCorr.paginate(correlations).map(c => (
              <div key={c.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Badge label={c.correlationType || 'co_occurrence'} color={CORR_COLORS[c.correlationType] || STONE[500]} />
                  <span style={{ fontWeight: 600, color: STONE[900] }}>{c.causeEventType}</span>
                  <span style={{ color: BRAND.primary, fontWeight: 700 }}>→</span>
                  <span style={{ fontWeight: 600, color: STONE[900], flex: 1 }}>{c.effectEventType}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: STONE[400] }}>{((c.confidence || 0) * 100).toFixed(0)}%</span>
                    {confidenceBar(c.confidence)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: STONE[400] }}>
                  <span>Observations: {c.observationCount}</span>
                  {c.avgDelayMs != null && <span>Avg delay: {c.avgDelayMs < 1000 ? c.avgDelayMs + 'ms' : (c.avgDelayMs / 1000).toFixed(1) + 's'}</span>}
                  {c.lastObservedAt && <span>Last: {new Date(c.lastObservedAt).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
            <Pagination totalItems={correlations.length} page={pgCorr.page} pageSize={pgCorr.pageSize} onPageChange={pgCorr.setPage} onPageSizeChange={pgCorr.setPageSize} noun="correlations" />
          </Card>
        </div>
      )}

      {/* ═══ RESOLUTION SUB-TAB ═══ */}
      {subTab === 'resolution' && (
        <div>
          <Card>
            <SectionHeader title="Resolution Patterns" badge={`${patterns.filter(p => p.patternType === 'RESOLUTION').length}`} />
            {patterns.filter(p => p.patternType === 'RESOLUTION').length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No resolution patterns yet. The system learns from remediation outcomes.
              </div>
            ) : patterns.filter(p => p.patternType === 'RESOLUTION').map(p => (
              <div key={p.id} style={{ padding: '12px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Badge label="RESOLUTION" color={TYPE_COLORS.RESOLUTION} />
                  <span style={{ fontWeight: 600, color: STONE[900], flex: 1, fontSize: 13 }}>{p.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: STONE[400] }}>{((p.confidence || 0) * 100).toFixed(0)}%</span>
                    {confidenceBar(p.confidence)}
                  </div>
                </div>
                {p.description && <div style={{ color: STONE[500], fontSize: 11, marginBottom: 6 }}>{p.description}</div>}

                {/* Success rate bar */}
                {p.successRate != null && p.successRate > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: STONE[500], width: 70 }}>Success rate</span>
                    <div style={{ flex: 1, maxWidth: 200, height: 8, background: STONE[100], borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${(p.successRate || 0) * 100}%`, height: '100%', borderRadius: 4,
                        background: p.successRate >= 0.7 ? '#059669' : p.successRate >= 0.4 ? '#D97706' : '#DC2626',
                      }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: p.successRate >= 0.7 ? '#059669' : p.successRate >= 0.4 ? '#D97706' : '#DC2626' }}>
                      {(p.successRate * 100).toFixed(0)}%
                    </span>
                  </div>
                )}

                {/* Alternative resolutions */}
                {p.alternativeActions && Array.isArray(p.alternativeActions) && p.alternativeActions.length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: STONE[500], fontWeight: 600 }}>Alternatives:</span>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      {p.alternativeActions.map((alt, i) => (
                        <span key={i} style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 4,
                          background: STONE[100], color: STONE[600],
                          border: `1px solid ${STONE[200]}`,
                        }}>
                          {alt.name || alt.effectiveness} ({((alt.successRate || 0) * 100).toFixed(0)}%)
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: STONE[400] }}>
                  <span>Observations: {p.observationCount}</span>
                  {p.averageEffectiveness != null && <span>Avg delta: {p.averageEffectiveness.toFixed(1)}</span>}
                  {p.failureContexts && Array.isArray(p.failureContexts) && <span>Failure contexts: {p.failureContexts.length}</span>}
                  {p.lastObservedAt && <span>Last: {new Date(p.lastObservedAt).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

/* ─── Remediation Engine Tab (Phase 2) ──────────────────────── */
function RemediationTab() {
  const [stats, setStats] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [activeExecs, setActiveExecs] = useState([]);
  const [subTab, setSubTab] = useState('dashboard'); // dashboard | queue | history | playbooks | actions
  const [loading, setLoading] = useState(true);
  const pgHistory = usePaginationState(10);
  const pgPlaybooks = usePaginationState(10);
  const [healConnectorId, setHealConnectorId] = useState('');
  const [retryRunId, setRetryRunId] = useState('');
  const [alertForm, setAlertForm] = useState({ alertType: '', targetName: '', severity: 'medium' });

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.intelligenceRemediationStats(),
      api.intelligenceRemediationExecutions(48),
      api.intelligenceRemediationPlaybooks(),
      api.intelligenceRemediationActive(),
    ]).then(([s, e, p, a]) => {
      if (s.ok) setStats(s.data);
      if (e.ok) setExecutions(e.data);
      if (p.ok) setPlaybooks(p.data);
      if (a.ok) setActiveExecs(a.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => { refresh(); const iv = setInterval(refresh, 8000); return () => clearInterval(iv); }, [refresh]);

  const handleHealConnector = async () => {
    if (!healConnectorId.trim()) return;
    const res = await api.intelligenceHealConnector(healConnectorId.trim());
    if (res.ok) { setHealConnectorId(''); refresh(); }
  };

  const handleRetryWorkflow = async () => {
    if (!retryRunId.trim()) return;
    const res = await api.intelligenceRetryWorkflow(retryRunId.trim());
    if (res.ok) { setRetryRunId(''); refresh(); }
  };

  const handleTriggerAlert = async () => {
    if (!alertForm.alertType || !alertForm.targetName) return;
    const res = await api.intelligenceTriggerAlert(alertForm.alertType, alertForm.targetName, alertForm.severity);
    if (res.ok) { setAlertForm({ alertType: '', targetName: '', severity: 'medium' }); refresh(); }
  };

  const handleCancel = async (id) => {
    await api.intelligenceCancelRemediation(id);
    refresh();
  };

  const handleRerun = async (id) => {
    await api.intelligenceRerunRemediation(id);
    refresh();
  };

  const execStatusColor = (status) => {
    if (status === 'completed') return STONE[700];
    if (status === 'failed') return BRAND.primary;
    if (status === 'cancelled' || status === 'rolled_back') return STONE[400];
    if (status === 'running' || status.startsWith('step_')) return STONE[600];
    return STONE[500];
  };

  const execTypeLabel = (t) => ({
    connector_heal: 'Connector Heal',
    workflow_retry: 'Workflow Retry',
    system_workflow_trigger: 'System Workflow',
    credential_refresh: 'Credential Refresh',
    send_alert: 'Send Alert',
    custom: 'Custom',
  }[t] || t);

  const execTypeIcon = (t) => ({
    connector_heal: '🔌', workflow_retry: '🔄', system_workflow_trigger: '⚡',
    credential_refresh: '🔑', send_alert: '📧', custom: '🔧',
  }[t] || '⚙');

  const subTabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'queue', label: 'Active Queue', badge: activeExecs.length },
    { id: 'history', label: 'History' },
    { id: 'playbooks', label: 'Playbooks' },
    { id: 'actions', label: 'Quick Actions' },
  ];

  if (loading && !stats) return <div style={{ padding: 40, color: STONE[400], textAlign: 'center' }}>Loading Remediation Engine...</div>;

  return (
    <div>
      {/* Sub-tab navigation */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            border: `1px solid ${subTab === t.id ? BRAND.primary : STONE[200]}`,
            background: subTab === t.id ? BRAND.light : '#fff',
            color: subTab === t.id ? BRAND.primary : STONE[600],
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {t.label}
            {t.badge > 0 && <span style={{
              background: BRAND.primary, color: '#fff', borderRadius: 99, padding: '1px 7px',
              fontSize: 10, fontWeight: 700, minWidth: 18, textAlign: 'center',
            }}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* ── Dashboard ── */}
      {subTab === 'dashboard' && stats && (
        <div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
            <StatCard label="Total (24h)" value={stats.total_24h || 0} subtitle="remediations triggered" />
            <StatCard label="Active" value={stats.active || 0} subtitle="currently running" />
            <StatCard label="Completed" value={stats.completed_24h || 0} subtitle="last 24 hours" />
            <StatCard label="Failed" value={stats.failed_24h || 0} subtitle="last 24 hours" />
            <StatCard label="Queue" value={stats.queue_size || 0} subtitle="awaiting execution" />
          </div>

          {/* By-type breakdown */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
            <Card style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: STONE[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>By Type (24h)</div>
              {stats.by_type && Object.entries(stats.by_type).map(([type, count]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${STONE[100]}` }}>
                  <span style={{ fontSize: 13, color: STONE[700] }}>{execTypeIcon(type)} {execTypeLabel(type)}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: STONE[900] }}>{count}</span>
                </div>
              ))}
              {(!stats.by_type || Object.keys(stats.by_type).length === 0) && (
                <div style={{ fontSize: 12, color: STONE[400], textAlign: 'center', padding: 20 }}>No remediations in last 24h</div>
              )}
            </Card>

            <Card style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: STONE[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Playbooks</div>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 750, color: STONE[900] }}>{stats.playbooks_enabled || 0}</div>
                  <div style={{ fontSize: 11, color: STONE[500] }}>Enabled</div>
                </div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 750, color: STONE[700] }}>{stats.playbooks_system || 0}</div>
                  <div style={{ fontSize: 11, color: STONE[500] }}>System</div>
                </div>
              </div>
            </Card>

            <Card style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: STONE[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Avg Duration</div>
              {stats.avg_durations_ms && Object.entries(stats.avg_durations_ms).map(([type, ms]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: 12, color: STONE[600] }}>{execTypeLabel(type)}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: STONE[900] }}>{(ms / 1000).toFixed(1)}s</span>
                </div>
              ))}
              {(!stats.avg_durations_ms || Object.keys(stats.avg_durations_ms).length === 0) && (
                <div style={{ fontSize: 12, color: STONE[400], textAlign: 'center', padding: 10 }}>No data yet</div>
              )}
            </Card>
          </div>

          {/* Recent activity preview */}
          <Card>
            <div style={{ fontSize: 11, fontWeight: 600, color: STONE[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Recent Activity</div>
            {executions.slice(0, 5).map(exec => (
              <div key={exec.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${STONE[100]}` }}>
                <span style={{ fontSize: 16 }}>{execTypeIcon(exec.executionType)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: STONE[900] }}>{exec.targetName || exec.executionType}</div>
                  <div style={{ fontSize: 11, color: STONE[500] }}>{execTypeLabel(exec.executionType)} — {exec.triggeredBy}</div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                  background: execStatusColor(exec.status) + '18', color: execStatusColor(exec.status),
                }}>{exec.status}</span>
                <span style={{ fontSize: 11, color: STONE[400] }}>
                  {exec.currentStep}/{exec.totalSteps}
                </span>
              </div>
            ))}
            {executions.length === 0 && <div style={{ fontSize: 12, color: STONE[400], textAlign: 'center', padding: 20 }}>No recent remediations</div>}
          </Card>
        </div>
      )}

      {/* ── Active Queue ── */}
      {subTab === 'queue' && (
        <div>
          {activeExecs.length === 0 ? (
            <Card><div style={{ textAlign: 'center', padding: 40, color: STONE[400] }}>No active remediations</div></Card>
          ) : activeExecs.map(exec => (
            <Card key={exec.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>{execTypeIcon(exec.executionType)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: STONE[900] }}>{exec.targetName}</div>
                  <div style={{ fontSize: 12, color: STONE[500] }}>{execTypeLabel(exec.executionType)}</div>
                </div>
                <button onClick={() => handleCancel(exec.id)} style={{
                  padding: '4px 12px', borderRadius: 6, border: `1px solid ${BRAND.primary}`,
                  background: '#fff', color: BRAND.primary, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}>Cancel</button>
              </div>
              {/* Step progress */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {Array.from({ length: exec.totalSteps }, (_, i) => {
                  const stepNum = i + 1;
                  const isCompleted = stepNum < exec.currentStep || (stepNum === exec.currentStep && exec.status === 'completed');
                  const isCurrent = stepNum === exec.currentStep && exec.status !== 'completed';
                  return (
                    <div key={i} style={{
                      flex: 1, height: 6, borderRadius: 3,
                      background: isCompleted ? STONE[700] : isCurrent ? BRAND.primary : STONE[200],
                      transition: 'background 0.3s',
                    }} />
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: STONE[500] }}>
                Step {exec.currentStep} of {exec.totalSteps} — {exec.status}
                {exec.triggeredBy && <span> — triggered by {exec.triggeredBy}</span>}
              </div>
              {/* Step results */}
              {exec.stepResults && Array.isArray(exec.stepResults) && exec.stepResults.map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, paddingLeft: 8 }}>
                  <span style={{ fontSize: 10, color: step.status === 'completed' ? '#10B981' : step.status === 'failed' ? BRAND.primary : STONE[400] }}>
                    {step.status === 'completed' ? '✓' : step.status === 'failed' ? '✗' : '○'}
                  </span>
                  <span style={{ fontSize: 12, color: STONE[700] }}>{step.action}</span>
                  <span style={{ fontSize: 10, color: STONE[400] }}>{step.status}</span>
                </div>
              ))}
            </Card>
          ))}
        </div>
      )}

      {/* ── History ── */}
      {subTab === 'history' && (
        <Card>
          <div style={{ fontSize: 11, fontWeight: 600, color: STONE[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Execution History (48h)</div>
          {pgHistory.paginate(executions).map(exec => (
            <div key={exec.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${STONE[100]}` }}>
              <span style={{ fontSize: 16 }}>{execTypeIcon(exec.executionType)}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: STONE[900] }}>{exec.targetName || 'Unknown'}</div>
                <div style={{ fontSize: 11, color: STONE[500] }}>
                  {execTypeLabel(exec.executionType)} — {exec.triggeredBy}
                  {exec.durationMs && <span> — {(exec.durationMs / 1000).toFixed(1)}s</span>}
                </div>
                {exec.errorMessage && <div style={{ fontSize: 11, color: BRAND.primary, marginTop: 2 }}>{exec.errorMessage}</div>}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                background: execStatusColor(exec.status) + '18', color: execStatusColor(exec.status),
              }}>{exec.status}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {(exec.status === 'completed' || exec.status === 'failed') && (
                  <button onClick={() => handleRerun(exec.id)} style={{
                    padding: '3px 10px', borderRadius: 6, border: `1px solid ${STONE[200]}`,
                    background: '#fff', color: STONE[700], fontSize: 11, cursor: 'pointer',
                  }}>Re-run</button>
                )}
              </div>
            </div>
          ))}
          {executions.length === 0 && <div style={{ fontSize: 12, color: STONE[400], textAlign: 'center', padding: 30 }}>No execution history</div>}
          <Pagination totalItems={executions.length} page={pgHistory.page} pageSize={pgHistory.pageSize} onPageChange={pgHistory.setPage} onPageSizeChange={pgHistory.setPageSize} noun="executions" />
        </Card>
      )}

      {/* ── Playbooks ── */}
      {subTab === 'playbooks' && (
        <div>
          {pgPlaybooks.paginate(playbooks).map(pb => (
            <Card key={pb.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: pb.enabled ? BRAND.light : STONE[100], fontSize: 18,
                }}>
                  {pb.targetType === 'connector' ? '🔌' : pb.targetType === 'workflow' ? '🔄' : '⚡'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: STONE[900] }}>{pb.name}</span>
                    {pb.system && <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                      background: STONE[200], color: STONE[600],
                    }}>SYSTEM</span>}
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                      background: pb.riskLevel === 'low' ? '#10B98118' : pb.riskLevel === 'medium' ? '#D9770618' : '#DC262618',
                      color: pb.riskLevel === 'low' ? '#10B981' : pb.riskLevel === 'medium' ? '#D97706' : '#DC2626',
                    }}>{pb.riskLevel.toUpperCase()} RISK</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                      background: pb.enabled ? '#10B98118' : STONE[200],
                      color: pb.enabled ? '#10B981' : STONE[400],
                    }}>{pb.enabled ? 'ENABLED' : 'DISABLED'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: STONE[500], marginTop: 4 }}>{pb.description}</div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                    <span style={{ fontSize: 11, color: STONE[400] }}>Runs: <strong style={{ color: STONE[700] }}>{pb.executionCount}</strong></span>
                    <span style={{ fontSize: 11, color: STONE[400] }}>Success: <strong style={{ color: '#10B981' }}>{pb.successRate?.toFixed(0) || 0}%</strong></span>
                    {pb.avgDurationMs && <span style={{ fontSize: 11, color: STONE[400] }}>Avg: <strong style={{ color: STONE[700] }}>{(pb.avgDurationMs / 1000).toFixed(1)}s</strong></span>}
                    <span style={{ fontSize: 11, color: STONE[400] }}>Target: <strong style={{ color: STONE[700] }}>{pb.targetType}</strong></span>
                    {!pb.requiresApproval && <span style={{ fontSize: 11, color: '#10B981', fontWeight: 600 }}>Auto-execute</span>}
                  </div>
                  {/* Steps preview */}
                  {pb.steps && Array.isArray(pb.steps) && (
                    <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {pb.steps.map((step, i) => (
                        <div key={i} style={{
                          fontSize: 10, padding: '3px 8px', borderRadius: 6,
                          background: STONE[100], color: STONE[600], display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          <span style={{ fontWeight: 700 }}>{step.step || (i+1)}</span>
                          <span>{step.description || step.action}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
          {playbooks.length === 0 && <Card><div style={{ textAlign: 'center', padding: 30, color: STONE[400] }}>No playbooks configured</div></Card>}
          <Pagination totalItems={playbooks.length} page={pgPlaybooks.page} pageSize={pgPlaybooks.pageSize} onPageChange={pgPlaybooks.setPage} onPageSizeChange={pgPlaybooks.setPageSize} noun="playbooks" />
        </div>
      )}

      {/* ── Quick Actions ── */}
      {subTab === 'actions' && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {/* Heal Connector */}
          <Card style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>🔌</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: STONE[900], marginBottom: 4 }}>Heal Connector</div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>Run the full healing pipeline: health check, credential refresh, reconnect, verify</div>
            <input
              value={healConnectorId}
              onChange={e => setHealConnectorId(e.target.value)}
              placeholder="Connector UUID"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${STONE[200]}`,
                fontSize: 12, marginBottom: 8, boxSizing: 'border-box', outline: 'none',
              }}
            />
            <button onClick={handleHealConnector} disabled={!healConnectorId.trim()} style={{
              width: '100%', padding: '8px 16px', borderRadius: 8,
              background: healConnectorId.trim() ? BRAND.primary : STONE[200],
              color: healConnectorId.trim() ? '#fff' : STONE[400],
              border: 'none', fontSize: 12, fontWeight: 700, cursor: healConnectorId.trim() ? 'pointer' : 'default',
            }}>Trigger Healing</button>
          </Card>

          {/* Retry Workflow */}
          <Card style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>🔄</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: STONE[900], marginBottom: 4 }}>Retry Workflow</div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>Retry a failed workflow from its last failed step, preserving completed outputs</div>
            <input
              value={retryRunId}
              onChange={e => setRetryRunId(e.target.value)}
              placeholder="Failed Run UUID"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${STONE[200]}`,
                fontSize: 12, marginBottom: 8, boxSizing: 'border-box', outline: 'none',
              }}
            />
            <button onClick={handleRetryWorkflow} disabled={!retryRunId.trim()} style={{
              width: '100%', padding: '8px 16px', borderRadius: 8,
              background: retryRunId.trim() ? BRAND.primary : STONE[200],
              color: retryRunId.trim() ? '#fff' : STONE[400],
              border: 'none', fontSize: 12, fontWeight: 700, cursor: retryRunId.trim() ? 'pointer' : 'default',
            }}>Trigger Retry</button>
          </Card>

          {/* Send Alert */}
          <Card style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>📧</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: STONE[900], marginBottom: 4 }}>Send Alert</div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>Trigger system workflow to send alert notifications to configured recipients</div>
            <input
              value={alertForm.alertType}
              onChange={e => setAlertForm(f => ({...f, alertType: e.target.value}))}
              placeholder="Alert type (e.g. connector_down)"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${STONE[200]}`,
                fontSize: 12, marginBottom: 6, boxSizing: 'border-box', outline: 'none',
              }}
            />
            <input
              value={alertForm.targetName}
              onChange={e => setAlertForm(f => ({...f, targetName: e.target.value}))}
              placeholder="Target name"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${STONE[200]}`,
                fontSize: 12, marginBottom: 6, boxSizing: 'border-box', outline: 'none',
              }}
            />
            <select
              value={alertForm.severity}
              onChange={e => setAlertForm(f => ({...f, severity: e.target.value}))}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${STONE[200]}`,
                fontSize: 12, marginBottom: 8, boxSizing: 'border-box', outline: 'none', background: '#fff',
              }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <button onClick={handleTriggerAlert} disabled={!alertForm.alertType || !alertForm.targetName} style={{
              width: '100%', padding: '8px 16px', borderRadius: 8,
              background: (alertForm.alertType && alertForm.targetName) ? BRAND.primary : STONE[200],
              color: (alertForm.alertType && alertForm.targetName) ? '#fff' : STONE[400],
              border: 'none', fontSize: 12, fontWeight: 700,
              cursor: (alertForm.alertType && alertForm.targetName) ? 'pointer' : 'default',
            }}>Send Alert</button>
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * Phase architecture — 4 primary tabs + Admin
 */
const PHASES = [
  {
    key: 'foundation',
    phase: 1,
    title: 'Foundation',
    headline: 'Self-Learning',
    icon: '◎',
    desc: 'Observes events, maps relationships, tracks what works',
    color: '#57534e',
  },
  {
    key: 'hands',
    phase: 2,
    title: 'Hands',
    headline: 'Self-Optimizing',
    icon: '⟳',
    desc: 'Heals connectors, retries workflows, enforces policy',
    color: '#0d9488',
  },
  {
    key: 'memory',
    phase: 3,
    title: 'Memory',
    headline: 'Self-Innovating',
    icon: '◈',
    desc: 'Detects patterns, infers causation, remembers resolutions',
    color: '#78716c',
  },
  {
    key: 'autonomy',
    phase: 4,
    title: 'Autonomy',
    headline: 'Self-Governing',
    icon: '◉',
    desc: 'Forecasts health, optimizes queues, detects anomalies',
    color: '#0a766b',
  },
];

const PHASE_LABELS = {
  foundation: 'Self-Learning',
  hands: 'Self-Optimizing',
  memory: 'Self-Innovating',
  autonomy: 'Self-Governing',
  admin: 'Administration',
};

export default function IntelligenceCenterPage() {
  const { isSuperAdmin } = useAuth();
  const nav = useNavigation();
  const [dashboard, setDashboard] = useState(null);
  const [events, setEvents] = useState([]);
  const [actions, setActions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = usePersistedState('intelligence.activePhase', 'foundation');
  const [timeRange, setTimeRange] = usePersistedState('intelligence.timeRange', 24);
  const [autonomyMode, setAutonomyMode] = usePersistedState('intelligence.autonomyMode', 'recommend');
  const [chatSessionId, setChatSessionId] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [systemSkills, setSystemSkills] = useState([]);
  const [proactiveStats, setProactiveStats] = useState(null);
  const [forecasts, setForecasts] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [optimizedQueue, setOptimizedQueue] = useState([]);
  // Pagination states for each list
  const pgEvents = usePaginationState(10);
  const pgActions = usePaginationState(10);
  const pgInsights = usePaginationState(10);
  const pgTransitions = usePaginationState(10);
  const pgBlocked = usePaginationState(10);
  const pgWorkflows = usePaginationState(10);
  const pgForecasts = usePaginationState(10);
  const pgQueue = usePaginationState(10);
  const pgRecs = usePaginationState(10);
  const pgAnomalies = usePaginationState(10);

  // Bulk selection state
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [selectedInsights, setSelectedInsights] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const toggleAction = (id) => setSelectedActions(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleInsight = (id) => setSelectedInsights(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleBulkApprove = async () => {
    if (selectedActions.size === 0 || bulkProcessing) return;
    setBulkProcessing(true);
    await api.intelligenceBulkApprove([...selectedActions]);
    setSelectedActions(new Set());
    setBulkProcessing(false);
    loadAll();
  };
  const handleBulkDismiss = async () => {
    if (selectedActions.size === 0 || bulkProcessing) return;
    setBulkProcessing(true);
    await api.intelligenceBulkDismiss([...selectedActions]);
    setSelectedActions(new Set());
    setBulkProcessing(false);
    loadAll();
  };
  const handleBulkRollback = async () => {
    if (selectedActions.size === 0 || bulkProcessing) return;
    setBulkProcessing(true);
    await api.intelligenceBulkRollback([...selectedActions]);
    setSelectedActions(new Set());
    setBulkProcessing(false);
    loadAll();
  };
  const handleBulkAcknowledge = async () => {
    if (selectedInsights.size === 0 || bulkProcessing) return;
    setBulkProcessing(true);
    await api.intelligenceBulkAcknowledge([...selectedInsights]);
    setSelectedInsights(new Set());
    setBulkProcessing(false);
    loadAll();
  };

  const loadAll = useCallback(async () => {
    try {
      const [dashRes, evtRes, actRes, insRes] = await Promise.all([
        apiFetch(`/api/intelligence/dashboard?hours=${timeRange}`),
        apiFetch(`/api/intelligence/events?hours=${timeRange}`),
        api.intelligenceActions(timeRange),
        api.intelligenceInsights(timeRange),
      ]);
      console.log('[IntelligenceCenter] dashboard raw:', { ok: dashRes?.ok, hasData: !!dashRes?.data, error: dashRes?.error });
      if (dashRes?.ok) {
        setDashboard(dashRes.data);
      } else {
        console.error('[IntelligenceCenter] dashboard fetch FAILED:', dashRes?.error);
        setDashboard(dashRes?.data || dashRes || {});
      }
      const evtArr = evtRes?.data || evtRes;
      const actArr = actRes?.data || actRes;
      const insArr = insRes?.data || insRes;
      setEvents(Array.isArray(evtArr) ? evtArr : []);
      setActions(Array.isArray(actArr) ? actArr : []);
      setInsights(Array.isArray(insArr) ? insArr : []);
      const intelligence = (dashRes?.data || dashRes)?.intelligence;
      if (intelligence?.autonomyMode) setAutonomyMode(intelligence.autonomyMode);
    } catch (err) {
      console.error('Intelligence Center load failed:', err);
    }
  }, [timeRange]);

  // Fetch system agent skills from API (not hardcoded)
  useEffect(() => {
    apiFetch('/api/intelligence/system-agent')
      .then(r => {
        console.log('[IntelligenceCenter] system-agent raw response:', JSON.stringify(r).slice(0, 500));
        if (!r?.ok) {
          console.error('[IntelligenceCenter] system-agent fetch FAILED:', r?.error || r);
          setSystemSkills([]);
          return;
        }
        const data = r?.data ?? r;
        const skills = data?.skills ?? [];
        console.log('[IntelligenceCenter] system-agent loaded:', { skillCount: skills.length, agentName: data?.agent?.name });
        setSystemSkills(skills);
      })
      .catch(err => {
        console.error('[IntelligenceCenter] system-agent fetch error:', err);
        setSystemSkills([]);
      });
  }, []);

  // Phase 4 — lazy-load proactive data when phase is active
  useEffect(() => {
    if (activePhase !== 'autonomy') return;
    const load = async () => {
      try {
        const [statsRes, fcastRes, anomRes, recRes, qRes] = await Promise.all([
          api.intelligenceProactiveStats(),
          api.intelligenceForecasts(),
          api.intelligenceAnomalies(),
          api.intelligenceSkillRecommendations(),
          api.intelligenceOptimizedQueue(),
        ]);
        setProactiveStats(statsRes?.data || statsRes);
        const fc = fcastRes?.data || fcastRes; setForecasts(Array.isArray(fc) ? fc : []);
        const an = anomRes?.data || anomRes; setAnomalies(Array.isArray(an) ? an : []);
        const rc = recRes?.data || recRes; setRecommendations(Array.isArray(rc) ? rc : []);
        const qq = qRes?.data || qRes; setOptimizedQueue(Array.isArray(qq) ? qq : []);
      } catch (err) { console.error('Phase 4 load failed:', err); }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [activePhase]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    loadAll().finally(() => setLoading(false));
    const interval = setInterval(loadAll, 30000);
    return () => clearInterval(interval);
  }, [loadAll]);

  // Push active phase to navigation breadcrumb context
  useEffect(() => {
    const label = PHASE_LABELS[activePhase] || activePhase;
    if (activePhase !== 'foundation') {
      nav.replaceContext([{ label, key: 'phase' }]);
    } else {
      nav.clearContext();
    }
  }, [activePhase]); // eslint-disable-line

  const handleApprove = async (id) => { await api.intelligenceApproveAction(id); loadAll(); };
  const handleDismiss = async (id) => { await api.intelligenceDismissAction(id); loadAll(); };
  const handleRollback = async (id) => { await api.intelligenceRollbackAction(id); loadAll(); };
  const handleAckInsight = async (id) => { await api.intelligenceAcknowledgeInsight(id); loadAll(); };
  const handleModeChange = async (mode) => {
    await api.intelligenceSetAutonomyMode(mode);
    setAutonomyMode(mode);
    loadAll();
  };
  const handleDrillDown = (type, id) => setDrillDown({ type, id });

  if (!isSuperAdmin?.()) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: 16, background: STONE[100], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 16 }}>◎</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: STONE[900], marginBottom: 8 }}>Access Restricted</h2>
        <p style={{ color: STONE[500], fontSize: 14 }}>The Intelligence Center is restricted to Super Admin only.</p>
      </div>
    );
  }

  const intelligence = dashboard?.intelligence || {};
  const analytics = dashboard?.analytics || {};
  const connectorHealth = dashboard?.connectorHealth || {};
  const policyEnforcement = dashboard?.policyEnforcement || {};
  const systemAgent = dashboard?.systemAgent || {};
  const systemWorkflows = dashboard?.systemWorkflows || {};
  const eventStats = dashboard?.eventStats || {};
  const pendingCount = intelligence.pendingActions || 0;
  const unackedInsights = intelligence.unacknowledgedInsights || 0;

  if (drillDown) {
    return (
      <div style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <DrillDownPanel targetType={drillDown.type} targetId={drillDown.id} onClose={() => setDrillDown(null)} />
      </div>
    );
  }

  const SKILL_TYPE_LABELS = { workflow_skill: 'Workflow', connector_skill: 'Connector', policy_skill: 'Policy', standard: 'Standard' };

  return (
    <V2PageShell
      title="Intelligence Center"
      subtitle="AI-driven platform operations brain — analyze, debug, auto-fix, and rollback."
      tags={
        <>
          <V2Pill tone="trust" size="sm">System</V2Pill>
          <V2Pill tone="danger" size="sm">Super Admin</V2Pill>
        </>
      }
      actions={
        <V2Button
          variant={showChat ? 'primary' : 'secondary'}
          size="sm"
          iconLeft={<Sparkles size={14} />}
          onClick={() => setShowChat(!showChat)}
        >
          {showChat ? 'Hide AI Chat' : 'AI Chat'}
        </V2Button>
      }
    >
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto' }}>

          {/* Autonomy Mode */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {Object.entries(MODE_META).map(([mode, meta]) => {
              const active = autonomyMode === mode;
              return (
                <button key={mode} onClick={() => handleModeChange(mode)} style={{
                  flex: 1, padding: '14px 16px', borderRadius: 12,
                  border: active ? `2px solid ${BRAND.primary}` : `1px solid ${STONE[200]}`,
                  background: active ? BRAND.light : '#fff',
                  cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16, color: active ? BRAND.primary : STONE[400] }}>{meta.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: active ? BRAND.dark : STONE[600] }}>{meta.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: active ? STONE[600] : STONE[500] }}>{meta.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Operational Status Banner */}
          <div style={{
            background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.primary} 50%, ${BRAND.mid} 100%)`,
            borderRadius: 16, padding: '22px 32px', marginBottom: 24,
            display: 'flex', justifyContent: 'space-around', alignItems: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
            {[
              { label: 'Events', value: analytics.totalEvents || 0 },
              { label: 'Errors', value: analytics.errorCount || 0 },
              { label: 'Pending Actions', value: pendingCount },
              { label: 'Insights', value: unackedInsights },
              { label: 'Agents', value: analytics.totalAgents || 0 },
            ].map((m, i) => (
              <div key={i} style={{ textAlign: 'center', position: 'relative', zIndex: 1, flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em', marginBottom: 3, textTransform: 'uppercase' }}>{m.label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* ═══════════════════════════════════════════════════════
              PRIMARY PHASE TABS — 4 phases + Admin
              ═══════════════════════════════════════════════════════ */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                {PHASES.map((p) => {
                  const active = activePhase === p.key;
                  return (
                    <button key={p.key} onClick={() => setActivePhase(p.key)} style={{
                      flex: 1, padding: '12px 14px', borderRadius: '10px 10px 0 0',
                      border: active ? `2px solid ${BRAND.primary}` : `1px solid ${STONE[200]}`,
                      borderBottom: active ? `2px solid ${BRAND.tint}` : `1px solid ${STONE[200]}`,
                      background: active ? BRAND.tint : '#fff',
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      transition: 'all 0.15s', position: 'relative',
                      marginBottom: -1, zIndex: active ? 2 : 1,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 14, color: active ? BRAND.primary : STONE[400] }}>{p.icon}</span>
                        <span style={{ fontSize: 9, fontWeight: 800, color: active ? BRAND.primary : STONE[400],
                          letterSpacing: '0.06em', textTransform: 'uppercase' }}>Phase {p.phase}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: active ? BRAND.dark : STONE[600], marginBottom: 2 }}>
                        {p.headline}
                      </div>
                      <div style={{ fontSize: 10, color: active ? STONE[500] : STONE[400], lineHeight: 1.3 }}>{p.desc}</div>
                    </button>
                  );
                })}
                {/* Admin tab */}
                <button onClick={() => setActivePhase('admin')} style={{
                  padding: '12px 14px', borderRadius: '10px 10px 0 0', minWidth: 100,
                  border: activePhase === 'admin' ? `2px solid ${BRAND.primary}` : `1px solid ${STONE[200]}`,
                  borderBottom: activePhase === 'admin' ? `2px solid ${BRAND.tint}` : `1px solid ${STONE[200]}`,
                  background: activePhase === 'admin' ? BRAND.tint : '#fff',
                  cursor: 'pointer', textAlign: 'center', fontFamily: 'inherit',
                  transition: 'all 0.15s', position: 'relative',
                  marginBottom: -1, zIndex: activePhase === 'admin' ? 2 : 1,
                }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: activePhase === 'admin' ? BRAND.primary : STONE[400],
                    letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>Admin</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: activePhase === 'admin' ? BRAND.dark : STONE[500] }}>
                    Config
                  </div>
                </button>
              </div>
              {/* Time range */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, paddingBottom: 8, marginLeft: 16 }}>
                {[1, 6, 24, 72, 168].map(h => (
                  <button key={h} onClick={() => setTimeRange(h)} style={{
                    padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                    border: timeRange === h ? `1px solid ${BRAND.primary}` : `1px solid ${STONE[200]}`,
                    background: timeRange === h ? BRAND.primary : '#fff', color: timeRange === h ? '#fff' : STONE[500],
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>{h < 24 ? `${h}h` : h === 24 ? '24h' : h === 72 ? '3d' : '7d'}</button>
                ))}
              </div>
            </div>
            {/* Bottom border that connects to the active tab */}
            <div style={{ borderTop: `2px solid ${BRAND.light}` }} />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: STONE[400] }}>Loading Intelligence Center...</div>
          ) : (
            <>
              {/* ═══ PHASE 1: FOUNDATION — Self-Learning ═══ */}
              {activePhase === 'foundation' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Phase 1 banner */}
                  <div style={{
                    background: BRAND.light, borderRadius: 12, padding: '18px 24px',
                    border: `1px solid ${BRAND.muted}`,
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 750, marginBottom: 4, color: BRAND.dark }}>Phase 1 — Foundation: Self-Learning Intelligence</div>
                    <div style={{ fontSize: 12, color: STONE[500] }}>
                      Observes events, maps relationships, tracks what works, and builds the knowledge graph
                    </div>
                  </div>

                  {/* Section: Overview Stats */}
                  <div>
                    <SectionHeader title="Platform Overview" badge={`Last ${timeRange}h`} />
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                      <StatCard label="Total Events" value={analytics.totalEvents || 0} subtitle={`Last ${timeRange}h`} />
                      <StatCard label="Errors" value={analytics.errorCount || 0} />
                      <StatCard label="Policy Blocked" value={policyEnforcement.blockedCount || 0} />
                      <StatCard label="Active Agents" value={analytics.totalAgents || 0} />
                      <StatCard label="Connectors" value={analytics.totalConnectors || 0} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                      <Card>
                        <SectionHeader title="Events by Category" />
                        {Object.entries(eventStats?.byCategory || {}).map(([cat, count]) => (
                          <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${STONE[100]}` }}>
                            <span style={{ fontSize: 12, color: STONE[700], textTransform: 'capitalize' }}>{cat}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: STONE[900] }}>{count}</span>
                          </div>
                        ))}
                        {!Object.keys(eventStats?.byCategory || {}).length && <div style={{ color: STONE[400], fontSize: 12, padding: 16, textAlign: 'center' }}>No events yet</div>}
                      </Card>
                      <Card>
                        <SectionHeader title="Pending Actions" badge={`${pendingCount}`} />
                        {actions.filter(a => a.status === 'pending').slice(0, 5).map((a, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                            <Badge label={a.severity} color={SEV[a.severity]} />
                            <span style={{ flex: 1, color: STONE[900], fontWeight: 500 }}>{a.title}</span>
                            <button onClick={() => handleApprove(a.id)} style={btnStyle('primary')}>Approve</button>
                          </div>
                        ))}
                        {!actions.filter(a => a.status === 'pending').length && <div style={{ color: STONE[400], fontSize: 12, padding: 16, textAlign: 'center' }}>No pending actions</div>}
                      </Card>
                    </div>
                  </div>

                  {/* Section: Event Stream */}
                  <Card>
                    <SectionHeader title="Event Stream" badge={`${events.length}`} />
                    <div>
                      {pgEvents.paginate(events).map((e, i) => <EventRow key={e.id || i} event={e} onDrillDown={handleDrillDown} />)}
                      {!events.length && <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>No events in the last {timeRange}h</div>}
                    </div>
                    <Pagination totalItems={events.length} page={pgEvents.page} pageSize={pgEvents.pageSize} onPageChange={pgEvents.setPage} onPageSizeChange={pgEvents.setPageSize} noun="events" />
                  </Card>

                  {/* Section: Connector Health */}
                  <div>
                    <SectionHeader title="Connector Health" />
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                      {Object.entries(connectorHealth?.currentStatuses || {}).map(([status, count]) => (
                        <StatCard key={status} label={status.toUpperCase()} value={count} />
                      ))}
                    </div>
                    <Card>
                      <SectionHeader title="Health Check Transitions" />
                      {pgTransitions.paginate(connectorHealth?.transitions || []).map((item, i) => {
                        const statusColor = STATUS_MAP[item.status] || STONE[500];
                        return (
                          <div key={i} onClick={() => item.connectorId && handleDrillDown('connector', item.connectorId)}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12, cursor: item.connectorId ? 'pointer' : 'default' }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor }} />
                            <span style={{ flex: 1, fontWeight: 600, color: STONE[700] }}>{item.connectorName || 'Unknown'}</span>
                            <Badge label={item.status} color={statusColor} />
                            {item.responseTimeMs != null && <span style={{ color: STONE[400], fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>{item.responseTimeMs}ms</span>}
                            {item.transition && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: STONE[100], color: STONE[600] }}>{item.previousStatus} → {item.status}</span>}
                            {item.connectorId && <span style={{ fontSize: 10, color: STONE[600] }}>→</span>}
                          </div>
                        );
                      })}
                      {!(connectorHealth?.transitions || []).length && <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>No transitions — all connectors stable</div>}
                      <Pagination totalItems={(connectorHealth?.transitions || []).length} page={pgTransitions.page} pageSize={pgTransitions.pageSize} onPageChange={pgTransitions.setPage} onPageSizeChange={pgTransitions.setPageSize} noun="transitions" />
                    </Card>
                  </div>

                  {/* Section: Knowledge Graph */}
                  <div>
                    <SectionHeader title="Knowledge Graph — 3D Navigator" badge="Self-Learning"
                      action={<button onClick={() => api.intelligenceGraphRebuild()} style={btnStyle()}>Rebuild Graph</button>} />
                    <KnowledgeGraph3D onNodeSelect={(node) => {
                      if (node?.type && node?.id) handleDrillDown(node.type.toLowerCase(), node.id);
                    }} />
                    <GraphStatsPanel />
                  </div>

                  {/* Section: Outcome Tracking */}
                  <OutcomeTrackingTab />
                </div>
              )}

              {/* ═══ PHASE 2: HANDS — Self-Optimizing ═══ */}
              {activePhase === 'hands' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Phase 2 banner */}
                  <div style={{
                    background: BRAND.light, borderRadius: 12, padding: '18px 24px',
                    border: `1px solid ${BRAND.muted}`,
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 750, marginBottom: 4, color: BRAND.dark }}>Phase 2 — Hands: Self-Optimizing Intelligence</div>
                    <div style={{ fontSize: 12, color: STONE[500] }}>
                      Heals connectors, retries workflows, enforces policy, and manages remediation playbooks
                    </div>
                  </div>

                  {/* Section: Actions */}
                  <div>
                    <SectionHeader title="Intelligence Actions" badge={`${pendingCount} pending`} />
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                      <StatCard label="Total Actions" value={intelligence.totalActions24h || 0} subtitle="Last 24h" />
                      <StatCard label="Pending" value={pendingCount} />
                      <StatCard label="Completed" value={intelligence.completedActions24h || 0} />
                      <StatCard label="Rolled Back" value={intelligence.rolledBackActions24h || 0} />
                    </div>
                    <Card>
                      <SectionHeader title="Action Log" badge={`${actions.length}`} />
                      <BulkActionBar
                        count={selectedActions.size}
                        total={actions.length}
                        onSelectAll={() => setSelectedActions(new Set(actions.map(a => a.id)))}
                        onDeselectAll={() => setSelectedActions(new Set())}
                        actions={[
                          { label: bulkProcessing ? 'Processing...' : `Approve (${selectedActions.size})`, onClick: handleBulkApprove, variant: 'primary', disabled: bulkProcessing || ![...selectedActions].some(id => actions.find(a => a.id === id && a.status === 'pending')) },
                          { label: `Dismiss (${selectedActions.size})`, onClick: handleBulkDismiss, disabled: bulkProcessing || ![...selectedActions].some(id => actions.find(a => a.id === id && a.status === 'pending')) },
                          { label: `Rollback (${selectedActions.size})`, onClick: handleBulkRollback, disabled: bulkProcessing || ![...selectedActions].some(id => actions.find(a => a.id === id && a.status === 'completed' && a.rollbackData)) },
                        ]}
                      />
                      {pgActions.paginate(actions).map((a, i) => (
                        <ActionRow key={a.id || i} action={a}
                          onApprove={handleApprove} onDismiss={handleDismiss} onRollback={handleRollback}
                          selected={selectedActions.has(a.id)}
                          onToggleSelect={() => toggleAction(a.id)}
                        />
                      ))}
                      {!actions.length && <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>No actions recorded yet</div>}
                      <Pagination totalItems={actions.length} page={pgActions.page} pageSize={pgActions.pageSize} onPageChange={pgActions.setPage} onPageSizeChange={pgActions.setPageSize} noun="actions" />
                    </Card>
                  </div>

                  {/* Section: Remediation Engine */}
                  <RemediationTab />

                  {/* Section: Policy Engine */}
                  <div>
                    <SectionHeader title="Policy Engine" />
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <StatCard label="Total Decisions" value={policyEnforcement.totalDecisions || 0} />
                      <StatCard label="Blocked" value={policyEnforcement.blockedCount || 0} />
                    </div>
                    <Card style={{ marginBottom: 16 }}>
                      <SectionHeader title="By Policy Type" />
                      {Object.entries(policyEnforcement?.byPolicyType || {}).map(([type, decisions]) => (
                        <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0', borderBottom: `1px solid ${STONE[100]}` }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: STONE[900], width: 120, textTransform: 'capitalize' }}>{type}</span>
                          {Object.entries(decisions).map(([d, c]) => <Badge key={d} label={`${d}: ${c}`} color={d === 'blocked' ? BRAND.primary : STONE[700]} />)}
                        </div>
                      ))}
                    </Card>
                    <Card>
                      <SectionHeader title="Recent Blocked" />
                      {pgBlocked.paginate(policyEnforcement?.recentBlocked || []).map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                          <Badge label={item.decision} color={BRAND.primary} />
                          <Badge label={item.policyType} />
                          <span style={{ flex: 1, color: STONE[700] }}>{item.skillName || item.ruleName || 'System'}</span>
                          {item.violationReason && <span style={{ color: STONE[600], fontSize: 10 }}>{item.violationReason}</span>}
                        </div>
                      ))}
                      {!(policyEnforcement?.recentBlocked || []).length && <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>All operations compliant</div>}
                      <Pagination totalItems={(policyEnforcement?.recentBlocked || []).length} page={pgBlocked.page} pageSize={pgBlocked.pageSize} onPageChange={pgBlocked.setPage} onPageSizeChange={pgBlocked.setPageSize} noun="violations" />
                    </Card>
                  </div>

                </div>
              )}

              {/* ═══ PHASE 3: MEMORY — Self-Innovating ═══ */}
              {activePhase === 'memory' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Phase 3 banner */}
                  <div style={{
                    background: BRAND.light, borderRadius: 12, padding: '18px 24px',
                    border: `1px solid ${BRAND.muted}`,
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 750, marginBottom: 4, color: BRAND.dark }}>Phase 3 — Memory: Self-Innovating Intelligence</div>
                    <div style={{ fontSize: 12, color: STONE[500] }}>
                      Detects patterns, infers causation, remembers resolutions, and generates actionable insights
                    </div>
                  </div>

                  {/* Section: Pattern Memory */}
                  <PatternMemoryTab />

                  {/* Section: Intelligence Insights */}
                  <div>
                    <SectionHeader title="Intelligence Insights" badge={`${unackedInsights} unacknowledged`} />
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <StatCard label="Unacknowledged" value={unackedInsights} />
                      <StatCard label="Actionable" value={intelligence.actionableInsights || 0} />
                    </div>
                    <Card>
                      <SectionHeader title="Insight Stream" badge={`${insights.length}`} />
                      <BulkActionBar
                        count={selectedInsights.size}
                        total={insights.filter(ins => !ins.acknowledged).length}
                        onSelectAll={() => setSelectedInsights(new Set(insights.filter(ins => !ins.acknowledged).map(ins => ins.id)))}
                        onDeselectAll={() => setSelectedInsights(new Set())}
                        actions={[
                          { label: bulkProcessing ? 'Processing...' : `Acknowledge (${selectedInsights.size})`, onClick: handleBulkAcknowledge, variant: 'primary', disabled: bulkProcessing },
                        ]}
                      />
                      {pgInsights.paginate(insights).map((ins, i) => (
                        <InsightRow key={ins.id || i} insight={ins} onAck={handleAckInsight}
                          selected={selectedInsights.has(ins.id)}
                          onToggleSelect={!ins.acknowledged ? () => toggleInsight(ins.id) : undefined}
                        />
                      ))}
                      {!insights.length && <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>No insights generated yet — the AI learns over time</div>}
                      <Pagination totalItems={insights.length} page={pgInsights.page} pageSize={pgInsights.pageSize} onPageChange={pgInsights.setPage} onPageSizeChange={pgInsights.setPageSize} noun="insights" />
                    </Card>
                  </div>
                </div>
              )}

              {/* ═══ PHASE 4: AUTONOMY — Self-Governing ═══ */}
              {activePhase === 'autonomy' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Phase 4 banner */}
                  <div style={{
                    background: BRAND.light, borderRadius: 12, padding: '18px 24px',
                    border: `1px solid ${BRAND.muted}`,
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 750, marginBottom: 4, color: BRAND.dark }}>Phase 4 — Autonomy: Self-Protecting Intelligence</div>
                    <div style={{ fontSize: 12, color: STONE[500] }}>
                      Health forecasting, approval queue optimization, skill promotion recommendations, and security anomaly detection
                    </div>
                  </div>

                  {/* Stats Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                    <StatCard label="Active Forecasts" value={proactiveStats?.forecasting?.total || 0} />
                    <StatCard label="Pending Queue" value={proactiveStats?.approvalQueue?.pendingCount || 0} />
                    <StatCard label="Skill Recs" value={proactiveStats?.skillRecommendations?.total || 0} />
                    <StatCard label="Active Anomalies" value={proactiveStats?.anomalyDetection?.activeAnomalies || 0} />
                  </div>

                  {/* Health Forecasts */}
                  <Card>
                    <SectionHeader title="Health Forecasts" badge="Forecasting" />
                    {forecasts.length > 0 ? pgForecasts.paginate(forecasts).map((f, i) => (
                      <div key={f.id || i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
                      }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: f.riskScore >= 0.6 ? '#ef4444' : f.trendDirection === 'declining' ? '#f59e0b' : '#22c55e',
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: STONE[900], fontSize: 13 }}>
                            {f.resourceType}: {String(f.resourceId).substring(0, 8)}...
                          </div>
                          <div style={{ fontSize: 11, color: STONE[500], marginTop: 2 }}>
                            Predicted: {f.predictedHealth || '—'} · Trend: {f.trendDirection || '—'} · Velocity: {f.trendVelocity ? f.trendVelocity.toFixed(2) : '—'}/hr
                          </div>
                        </div>
                        <div style={{
                          padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          background: f.riskScore >= 0.6 ? '#fef2f2' : f.riskScore >= 0.3 ? '#fffbeb' : '#f0fdf4',
                          color: f.riskScore >= 0.6 ? '#dc2626' : f.riskScore >= 0.3 ? '#d97706' : '#16a34a',
                        }}>
                          Risk: {((f.riskScore || 0) * 100).toFixed(0)}%
                        </div>
                        <div style={{ fontSize: 11, color: STONE[400] }}>
                          conf: {((f.confidence || 0) * 100).toFixed(0)}%
                        </div>
                      </div>
                    )) : (
                      <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>
                        No forecasts generated yet. Health data will be analyzed every 15 minutes.
                      </div>
                    )}
                    <Pagination totalItems={forecasts.length} page={pgForecasts.page} pageSize={pgForecasts.pageSize} onPageChange={pgForecasts.setPage} onPageSizeChange={pgForecasts.setPageSize} noun="forecasts" />
                  </Card>

                  {/* Optimized Approval Queue */}
                  <Card>
                    <SectionHeader title="Priority-Optimized Approval Queue" badge="Queue" />
                    {optimizedQueue.length > 0 ? pgQueue.paginate(optimizedQueue).map((a, i) => (
                      <div key={a.id || i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 800, color: '#fff', width: 24, height: 24,
                          borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: i < 3 ? '#0d9488' : i < 6 ? '#f59e0b' : STONE[500],
                        }}>
                          {i + 1}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: STONE[900], fontSize: 13 }}>{a.title || a.actionType}</div>
                          <div style={{ fontSize: 11, color: STONE[500], marginTop: 2 }}>
                            Priority: {((a.priorityScore || 0) * 100).toFixed(0)}
                            {a.blastRadius ? ` · Blast radius: ${a.blastRadius}` : ''}
                            {a.timeSensitivity ? ` · ${a.timeSensitivity}` : ''}
                          </div>
                        </div>
                        <Badge label={a.status || 'pending'} color={STONE[700]} />
                      </div>
                    )) : (
                      <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>
                        Approval queue is empty. Pending actions will be priority-scored when created.
                      </div>
                    )}
                    <Pagination totalItems={optimizedQueue.length} page={pgQueue.page} pageSize={pgQueue.pageSize} onPageChange={pgQueue.setPage} onPageSizeChange={pgQueue.setPageSize} noun="queue items" />
                  </Card>

                  {/* Skill Promotion Recommendations */}
                  <Card>
                    <SectionHeader title="Skill Promotion Recommendations" badge="Skills" />
                    {recommendations.length > 0 ? pgRecs.paginate(recommendations).map((r, i) => {
                      const actionColors = { promote: '#059669', hold: '#D97706', regress: BRAND.primary, review_needed: STONE[600] };
                      return (
                        <div key={r.id || i} style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                          borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: STONE[900], fontSize: 13 }}>
                              {r.skillName || `Skill ${String(r.skillId).substring(0, 8)}`}
                            </div>
                            <div style={{ fontSize: 11, color: STONE[500], marginTop: 2 }}>
                              Current: {r.currentPhase || '—'} → Recommended: {r.targetPhase || '—'}
                            </div>
                          </div>
                          <div style={{ width: 80 }}>
                            <div style={{ height: 6, borderRadius: 3, background: STONE[100], overflow: 'hidden' }}>
                              <div style={{
                                height: '100%', borderRadius: 3, width: `${(r.readinessScore || 0) * 100}%`,
                                background: (r.readinessScore || 0) >= 0.7 ? '#22c55e' : (r.readinessScore || 0) >= 0.4 ? '#f59e0b' : '#ef4444',
                              }} />
                            </div>
                            <div style={{ fontSize: 10, color: STONE[400], marginTop: 2, textAlign: 'center' }}>
                              {((r.readinessScore || 0) * 100).toFixed(0)}% ready
                            </div>
                          </div>
                          <span style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: (actionColors[r.recommendedAction] || STONE[500]) + '18',
                            color: actionColors[r.recommendedAction] || STONE[600],
                          }}>
                            {r.recommendedAction || '—'}
                          </span>
                        </div>
                      );
                    }) : (
                      <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>
                        No skill recommendations yet. Analysis runs hourly.
                      </div>
                    )}
                    <Pagination totalItems={recommendations.length} page={pgRecs.page} pageSize={pgRecs.pageSize} onPageChange={pgRecs.setPage} onPageSizeChange={pgRecs.setPageSize} noun="recommendations" />
                  </Card>

                  {/* Anomaly Detection */}
                  <Card>
                    <SectionHeader title="Security Anomaly Detection" badge="Anomalies" />
                    {anomalies.length > 0 ? pgAnomalies.paginate(anomalies).map((a, i) => (
                      <div key={a.id || i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        borderBottom: `1px solid ${STONE[100]}`, fontSize: 12,
                      }}>
                        <span style={{ fontSize: 14 }}>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: STONE[900], fontSize: 13 }}>{a.metricName}</div>
                          <div style={{ fontSize: 11, color: STONE[500], marginTop: 2 }}>
                            {a.resourceType ? `${a.resourceType}: ${String(a.resourceId || '').substring(0, 8)}` : 'Global metric'}
                            {' · '}Mean: {(a.mean || 0).toFixed(2)} · Std: {(a.stddev || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{
                          padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                          background: '#fef2f2', color: '#dc2626',
                        }}>
                          z-score: {(a.zScore || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: 11, color: STONE[400] }}>
                          samples: {a.sampleCount || 0}
                        </div>
                      </div>
                    )) : (
                      <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>
                        No anomalies detected. Baselines are updated every 5 minutes.
                      </div>
                    )}
                    <Pagination totalItems={anomalies.length} page={pgAnomalies.page} pageSize={pgAnomalies.pageSize} onPageChange={pgAnomalies.setPage} onPageSizeChange={pgAnomalies.setPageSize} noun="anomalies" />
                  </Card>
                </div>
              )}

              {/* ═══ ADMIN — System Agent + AI Models ═══ */}
              {activePhase === 'admin' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Admin banner */}
                  <div style={{
                    background: BRAND.light, borderRadius: 12, padding: '18px 24px',
                    border: `1px solid ${BRAND.muted}`,
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 750, marginBottom: 4, color: BRAND.dark }}>Admin — Configuration & System Management</div>
                    <div style={{ fontSize: 12, color: STONE[500] }}>
                      System agent overview, AI model configuration, and system workflow management
                    </div>
                  </div>

                  {/* System Agent */}
                  <div>
                    <div style={{
                      background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.primary} 50%, ${BRAND.mid} 100%)`,
                      borderRadius: 16, padding: '26px 32px', marginBottom: 20,
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, position: 'relative', zIndex: 1 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#fff' }}>◉</div>
                        <div>
                          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 }}>{systemAgent.name || 'Agentlets System Agent'}</h3>
                          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            {['Phase: ' + (systemAgent.phase || 'act'), 'Status: ' + (systemAgent.status || 'active'), 'Skills: ' + (systemSkills.length || systemAgent.skillCount || 0)].map((t, i) => (
                              <span key={i} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>{t}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontFamily: "'IBM Plex Mono', monospace", position: 'relative', zIndex: 1 }}>
                        UUID: 00000000-0000-0000-0000-000000000001 · Immutable · Global Singleton · SuperAdmin Only
                      </div>
                    </div>

                    <Card>
                      <SectionHeader title="System Skills" badge={`${systemSkills.length} active`} />
                      {systemSkills.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {systemSkills.map(s => (
                            <div key={s.id || s.name} style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                              borderRadius: 8, background: STONE[50], border: `1px solid ${STONE[200]}`,
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: STONE[900], fontSize: 13, marginBottom: 2 }}>{s.name}</div>
                                {s.description && <div style={{ fontSize: 11, color: STONE[500] }}>{s.description}</div>}
                              </div>
                              <Badge label={SKILL_TYPE_LABELS[s.skillType] || s.skillType || 'standard'} color={STONE[600]} />
                              {s.category && <span style={{ fontSize: 10, color: STONE[400] }}>{s.category}</span>}
                              {s.linkedResourceId && (
                                <button
                                  onClick={() => handleDrillDown('workflow', s.linkedResourceId)}
                                  style={{ ...btnStyle(), fontSize: 9 }}>
                                  View Workflow →
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: STONE[400], fontSize: 12, padding: 16, textAlign: 'center' }}>No system skills found — check backend logs</div>
                      )}
                    </Card>
                  </div>

                  {/* AI Model Configuration */}
                  <div>
                    <SectionHeader title="AI Model Configuration" />
                    <AIModelConfigTab />
                  </div>

                  {/* Agentlet System Workflows */}
                  <div>
                    <SectionHeader title="Agentlet System Workflows" badge={`${systemWorkflows?.totalWorkflows || 0}`} />
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 16,
                      background: BRAND.tint, borderRadius: 8, border: `1px solid ${BRAND.muted}40`, fontSize: 12, color: STONE[500],
                    }}>
                      <span style={{ fontSize: 14 }}>💡</span>
                      <span>To promote a workflow to System status, open it in the <strong style={{ color: STONE[700] }}>Workflow Canvas</strong> and click the <strong style={{ color: BRAND.primary }}>★ System</strong> toggle in the toolbar.</span>
                    </div>
                    <Card>
                      {pgWorkflows.paginate(systemWorkflows?.workflows || []).map((wf, i) => (
                        <div key={wf.id || i}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${STONE[100]}`, fontSize: 12 }}>
                          <div style={{ flex: 1, cursor: wf.id ? 'pointer' : 'default' }} onClick={() => wf.id && handleDrillDown('workflow', wf.id)}>
                            <div style={{ fontWeight: 600, color: STONE[900], fontSize: 13 }}>
                              <span style={{ color: BRAND.primary, marginRight: 4 }}>★</span>
                              {wf.name}
                            </div>
                            <div style={{ fontSize: 11, color: STONE[500], marginTop: 2 }}>{wf.description}</div>
                          </div>
                          <Badge label={wf.status || 'ACTIVE'} color={wf.status === 'active' || wf.status === 'ACTIVE' ? '#16a34a' : STONE[700]} />
                          {wf.id && <span style={{ cursor: 'pointer', fontSize: 10, color: STONE[600] }} onClick={() => handleDrillDown('workflow', wf.id)}>→</span>}
                        </div>
                      ))}
                      {!(systemWorkflows?.workflows || []).length && (
                        <div style={{ color: STONE[400], fontSize: 12, padding: 32, textAlign: 'center' }}>
                          No system workflows yet. Open any workflow in the Canvas and toggle ★ System to promote it.
                        </div>
                      )}
                      <Pagination totalItems={(systemWorkflows?.workflows || []).length} page={pgWorkflows.page} pageSize={pgWorkflows.pageSize} onPageChange={pgWorkflows.setPage} onPageSizeChange={pgWorkflows.setPageSize} noun="workflows" />
                    </Card>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* AI Chat Sidebar */}
      {showChat && (
        <div style={{
          width: 420, borderLeft: `1px solid ${STONE[200]}`, background: '#fff',
          display: 'flex', flexDirection: 'column', flexShrink: 0,
        }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${STONE[100]}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: STONE[100], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>◉</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: STONE[900], flex: 1 }}>Intelligence AI</span>
            <button onClick={() => { setChatSessionId(null); }} style={btnStyle()}>New Chat</button>
            <button onClick={() => setShowChat(false)} style={btnStyle()}>Close</button>
          </div>
          <AIChatPanel sessionId={chatSessionId} setSessionId={setChatSessionId} onDrillDown={handleDrillDown} />
        </div>
      )}
    </div>
    </V2PageShell>
  );
}
